import { api, resetSessionExpiredFlag } from './api.js';
import { clearSessionHint } from './session.js';
import {
  STATUS_CFG, STATUS_ORDER, esc, getInitials, getAvatarColor, fmtDate, fmtAmt, isOverdue,
  todayISO, nowTime, whatsappUrl, showToast, hasPerm, isViewer, relativeTime,
  fmtDateTime, playReminderSound, initPasswordToggles, resetPasswordInput,
  PIPELINE_COLORS, getStatusesForType, getPipeline, STATUS_LABELS, STATUS_BUCKETS,
  isCompletedStatus, statusBucket, leadOutstandingBalance, effectiveSoldAmount, effectiveProfit, effectiveCostAmount, ticketQuotedTotal,
  isClosedStatus, applyNavAccess, canViewOwner, canDeleteLead, isLiteModeOn,
  visaBillablePax, visaCostTotal, syncTourAgentCost, isBookingExpired,
} from './utils.js';
import { renderStageForm, collectStageFormData, validateStageFormClient, initStageForm, formatStageHistoryDescription, statusHistoryTitle, statusHasStageForm, leadStageIncomplete, stageProceedBlocked, renderStageSavedViewHtml, stageTabShortLabel, stageHasSavedData, renderTicketPoolIntakeMainHtml } from './status-stage.js';
import { getStageHint, applyPipelineSettings } from './status-pipelines.js';
import { icon, setIcon } from './icons.js';
import {
  setListLoading, renderFilterChips, renderClickablePipeline, createLeadPrefetch,
  saveFilterForAgent, loadFilterForAgent, saveInquiryDraft, loadInquiryDraft,
  clearInquiryDraft, collectInquiryDraftFields, applyInquiryDraftFields,
  initDetailSwipe, initReduceEffectsToggle, toggleReduceEffects,
  getNextStatus, canQuickAdvance, syncScrollStrip,
} from './ux.js';
import { updateHeaderGreeting, startHeaderClock } from './page-chrome.js';
import {
  initThemeControls, toggleTheme, updateThemeIcon, updateThemeMeta,
} from './theme-ui.js';
import { toggleDensity } from './density-ui.js';
import {
  syncPerAdtUi, setAdtProfiles, calcTicketCostTotal, calcTicketPricingBreakdown, buildAdtExtraFields,
  applyAdtProfilesToBody, validateAdtProfiles, initTicketPaxUi, resetInquiryCardCollapse,
  formatTicketPax, formatAdtItinerarySummary, isPerAdtMode, shouldOfferPerAdtMode, travelerDisplayName,
} from './ticket-pax.js';
import { isTicketQuickIntake, ticketIntakeText, isUnassignedPoolTicket, isAutoTicketCustomerName, detailTicketStatusLabel } from './assistant-ticket-v2.js';

const state = {
  agent: null,
  agents: [],
  settings: { tours: [], visas: [], sources: [] },
  filter: { type: '', status: '', bucket: '', overdue: false, custom: false, agent_id: '', q: '', dept_scope: 'my' },
  page: 1,
  total_pages: 1,
  total: 0,
  leads: [],
  selected: new Set(),
  editingId: null,
  detailId: null,
  detailLead: null,
  detailAccess: null,
  detailHistory: null,
  statusPrev: null,
  pendingStatus: null,
  soundOn: localStorage.getItem('mysky_sound') !== '0',
  alertedReminders: new Set(),
  autoRefresh: localStorage.getItem('mysky_autorefresh') !== '0',
  refreshTimer: null,
  listFetchGen: 0,
  statsFetchGen: 0,
  focusedRowIdx: -1,
  leadPrefetchCache: new Map(),
  clientDupMatches: [],
  clientMatchSelected: null,
  clientMatchResolver: null,
  mergeDupLeads: [],
  clientDupAcknowledged: false,
  balanceCollectCtx: null,
  balancePostponeId: null,
  stagePanelUrgent: false,
  detailStageTab: null,
  historyFilter: 'all',
  historySearch: '',
  historyItems: [],
};

let inquiryModalReady = false;
let inquiryShellPromise = null;

export async function ensureInquiryModalShell() {
  if ($('addModal')) return;
  if (!inquiryShellPromise) {
    inquiryShellPromise = fetch('/includes/inquiry-modal.html').then(async (r) => {
      if (!r.ok) throw new Error('Could not load inquiry form');
      document.body.insertAdjacentHTML('beforeend', await r.text());
    });
  }
  await inquiryShellPromise;
}

/** Load + wire the inquiry modal on demand (first "New inquiry" / edit click). */
export async function ensureInquiryModalReady() {
  await ensureInquiryModalShell();
  initInquiryModal();
}

export function initInquiryModal() {
  if (!$('addModal') || inquiryModalReady) return;
  inquiryModalReady = true;
  initTicketPaxUi();
  document.querySelectorAll('.type-seg-btn').forEach((btn) => {
    btn.addEventListener('click', () => selectType(btn.dataset.type));
  });
  $('inquiryModal')?.addEventListener('click', (e) => {
    const btn = e.target.closest('.mini-seg-btn');
    if (btn) {
      e.preventDefault();
      if (btn.closest('#tripTypeSeg')) selectTripType(btn.dataset.value);
      else if (btn.closest('#dateFlexSeg')) selectDateFlexMode(btn.dataset.value);
      else if (btn.closest('#transitPrefSeg')) selectTransitPref(btn.dataset.value);
      else selectMiniSeg(btn);
    }
  });
  $('m_Package')?.addEventListener('change', onPackageChange);
  $('m_VisaCost')?.addEventListener('input', () => {
    if ($('m_VisaCost')) $('m_VisaCost').dataset.userEdited = '1';
  });
  $('addModal')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      saveLead(e.metaKey || e.ctrlKey);
    }
  });
  $('draftRestoreBtn')?.addEventListener('click', restoreInquiryDraft);
  $('draftDiscardBtn')?.addEventListener('click', discardInquiryDraft);
  const debouncedClientDupCheck = debounce(() => { checkClientDuplicates(); }, 400);
  ['m_Phone', 'm_Email', 'm_Passport'].forEach((id) => {
    $(id)?.addEventListener('input', debouncedClientDupCheck);
    $(id)?.addEventListener('blur', () => {
      if (id === 'm_Phone') applyPhoneFieldFormat();
      checkClientDuplicates();
    });
  });
  $('m_Phone')?.addEventListener('paste', (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text') || '';
    const el = e.target;
    el.value = formatPhoneDisplay(text);
    debouncedClientDupCheck();
  });
  $('addMultiCitySeg')?.addEventListener('click', addMultiCitySegment);
  $('multiCitySegments')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-seg-remove]');
    if (btn) removeMultiCitySegment(Number(btn.dataset.segRemove));
  });
  $('multiCitySegments')?.addEventListener('input', (e) => {
    if (!e.target.classList.contains('seg-dest')) return;
    const row = e.target.closest('.multi-city-seg');
    if (row == null) return;
    const idx = Number(row.dataset.segIdx);
    const rows = [...document.querySelectorAll('#multiCitySegments .multi-city-seg')];
    const nextOrigin = rows[idx + 1]?.querySelector('.seg-origin');
    if (nextOrigin && !nextOrigin.value.trim()) {
      nextOrigin.value = e.target.value.trim().toUpperCase();
    }
  });
  $('inquiryModal')?.addEventListener('blur', (e) => {
    if (e.target.matches('.route-code, .seg-origin, .seg-dest')) {
      e.target.value = e.target.value.trim().toUpperCase();
    }
  }, true);
}

export function isLeadsExplorerPage() {
  return !!document.querySelector('.leads-explorer-shell');
}

export function isLauncherPage() {
  return !!document.querySelector('.launcher-shell');
}

export function bindDetailHost(hostState, callbacks = {}) {
  state.agent = hostState.agent;
  state.agents = hostState.agents;
  state.settings = hostState.settings;
  state.leads = hostState.leads;
  hostListRefresh = callbacks.refreshList || null;
  hostStatsRefresh = callbacks.refreshStats || null;
  hostRenderList = callbacks.renderList || null;
}

export function syncDetailHostLeads(leads) {
  state.leads = leads;
}

let hostListRefresh = null;
let hostStatsRefresh = null;
let hostRenderList = null;

const leadPrefetch = createLeadPrefetch(state.leadPrefetchCache);

function isProPreview() {
  return document.documentElement.classList.contains('theme-pro');
}

function initProPreview() {
  /* Liquid glass preview on workers.dev — styling via pro.css */
}


const DEPARTMENTS = ['tour', 'visa', 'ticket'];
const DEPARTMENT_LABELS = { tour: 'Tours', visa: 'Visa', ticket: 'Tickets' };
const HOME_LIST_LIMIT = 8;

function showDepartmentScopeBar() {
  return state.agent
    && state.agent.role !== 'admin'
    && state.agent.departments?.length
    && state.agent.departments.length < DEPARTMENTS.length;
}

function effectiveDeptScope() {
  if (!state.agent || state.agent.role === 'admin') return 'all';
  if (state.filter.dept_scope === 'all') return 'all';
  if (state.filter.type) return 'all';
  return 'my';
}

function initDepartmentHome() {
  if (!state.agent || state.agent.role === 'admin') {
    state.filter.dept_scope = 'all';
  } else if (!state.filter.dept_scope) {
    state.filter.dept_scope = 'my';
  }
  applyDepartmentUI();
}

function applyDepartmentUI() {
  const bar = $('deptScopeBar');
  if (!bar) return;
  const show = showDepartmentScopeBar();
  bar.classList.toggle('hidden', !show);
  if (!show) return;
  const depts = (state.agent.departments || []).map((d) => DEPARTMENT_LABELS[d] || d).join(' · ');
  const hint = $('deptScopeHint');
  if (hint) {
    const primary = DEPARTMENT_LABELS[state.agent.primary_department] || state.agent.primary_department;
    hint.textContent = primary
      ? `Your work: ${depts} (primary: ${primary})`
      : `Your departments: ${depts}`;
  }
  $('deptScopeMy')?.classList.toggle('active', state.filter.dept_scope !== 'all');
  $('deptScopeAll')?.classList.toggle('active', state.filter.dept_scope === 'all');
  const mine = new Set(state.agent.departments || []);
  document.querySelectorAll('[data-filter="tour"],[data-filter="visa"],[data-filter="ticket"]').forEach((el) => {
    const inDept = mine.has(el.dataset.filter);
    el.classList.toggle('sidebar-item-my-dept', inDept);
    el.classList.toggle('sidebar-item-other-dept', !inDept);
  });
}

function setDepartmentScope(scope) {
  state.filter.dept_scope = scope === 'all' ? 'all' : 'my';
  persistFilter();
  applyDepartmentUI();
  refresh();
}

const $ = (id) => document.getElementById(id);

async function boot() {
  initPasswordToggles();
  initProPreview();
  initReduceEffectsToggle();
  updateReduceEffectsUI();
  updateThemeMeta();
  startHeaderClock();
  updateRefreshDot();
  initThemeControls();
  updateSoundIcon();
  initDetailSwipe($('detailPanel'), $('detailOverlay'), closeDetail);
  registerServiceWorker();

  try {
    const me = await api('/api/me');
    state.agent = me.agent;
    showApp();
    // The inquiry form is only needed once the user clicks "New inquiry";
    // warm it in the background instead of gating boot on it.
    ensureInquiryModalReady().catch((e) => console.error(e));
    await loadMeta();
    if (isLauncherPage()) {
      initLauncher();
      loadAccessInbox();
    }
    const params = new URLSearchParams(location.search);
    const openId = params.get('open');
    const wantEdit = params.get('edit') === '1';
    if (openId) {
      await openDetail(openId);
      if (wantEdit && state.detailAccess?.can_edit) {
        await openEditModal(openId);
      }
      if (wantEdit || openId) {
        history.replaceState(null, '', location.pathname);
      }
    }
  } catch {
    showLogin();
  }

  window.__myskyOn401 = () => {
    state.agent = null;
    showLogin();
    showToast('Session expired — please sign in again', 'warning');
  };

  $('loginForm')?.addEventListener('submit', onLogin);
  $('fpSaveBtn')?.addEventListener('click', saveForcedPassword);
  $('forcePassModal')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveForcedPassword(); }
  });
  $('dFollowup')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveFollowupOnly(); }
  });
  populateStatusSelects();

  document.querySelectorAll('.modal-overlay').forEach((overlay) => {
    overlay.addEventListener('click', (e) => {
      if (e.target !== overlay) return;
      const id = overlay.id;
      if (id === 'statusModal') cancelStatusChange();
      else if (id === 'forcePassModal') return;
      else if (id) closeModal(id);
    });
  });

  $('clientMatchNewBtn')?.addEventListener('click', onClientMatchNewEntry);
  $('clientMatchMergeBtn')?.addEventListener('click', onClientMatchMerge);
  $('mergeDupConfirmBtn')?.addEventListener('click', confirmMergeDuplicates);
  document.querySelectorAll('[data-history-filter]').forEach((btn) => {
    btn.addEventListener('click', () => setHistoryFilter(btn.dataset.historyFilter));
  });
  const debouncedHistorySearch = debounce(() => {
    state.historySearch = $('historySearch')?.value?.trim() || '';
    renderHistoryTimeline(state.historyItems);
  }, 200);
  $('historySearch')?.addEventListener('input', debouncedHistorySearch);

  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
      e.preventDefault();
      if (state.agent) openAddModal();
      return;
    }
    if (e.key === 'Escape') {
      if ($('historyModal')?.classList.contains('open')) closeModal('historyModal');
      else if ($('forcePassModal')?.style.display !== 'none' && $('forcePassModal')?.classList.contains('open')) { /* blocked */ }
      else if ($('editRequestModal')?.classList.contains('open')) closeModal('editRequestModal');
      else if ($('statusModal')?.classList.contains('open')) cancelStatusChange();
      else if ($('callModal')?.classList.contains('open')) closeModal('callModal');
      else if ($('addModal')?.classList.contains('open')) closeModal('addModal');
      else if ($('detailPanel')?.classList.contains('open')) closeDetail();
      return;
    }
    if (isTypingContext() || !state.agent) return;
    if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      $('launcherSearchInput')?.focus();
    }
  });

  exposeMyskyGlobal();
}

function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch(() => {});
}

function isTypingContext() {
  const t = document.activeElement;
  if (!t) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function isModalOpen() {
  if ($('detailPanel')?.classList.contains('open')) return true;
  return ['addModal', 'statusModal', 'callModal', 'historyModal', 'editRequestModal'].some(
    (id) => $(id)?.classList.contains('open')
  );
}

function onFilterChipsChange() {
  syncToolbarFromFilter();
  populateStatusSelects();
  syncPills();
  syncSidebarActive();
  state.page = 1;
  if (state.agent) saveFilterForAgent(state.agent.id, state.filter, state.page);
  refreshListOnly();
}

function persistFilter() {
  if (!state.agent) return;
  saveFilterForAgent(state.agent.id, state.filter, state.page);
  renderFilterChips(state.filter, state.agents, onFilterChipsChange);
}

function focusRow(idx) {
  if (!state.leads.length) return;
  state.focusedRowIdx = Math.max(0, Math.min(idx, state.leads.length - 1));
  renderTable();
  const lead = state.leads[state.focusedRowIdx];
  const row = $('leadsBody')?.querySelector(`tr[data-id="${lead?.id}"]`);
  row?.scrollIntoView({ block: 'nearest' });
}

function restoreSavedFilter() {
  const saved = loadFilterForAgent(state.agent?.id);
  if (!saved?.filter) return;
  Object.assign(state.filter, saved.filter);
  state.page = saved.page || 1;
  if (saved.filter.q && $('searchInput')) $('searchInput').value = saved.filter.q;
  syncToolbarFromFilter();
  syncPills();
  syncSidebarActive();
}

function maybeSaveInquiryDraft() {
  if (state.editingId || !state.agent) return;
  const fields = collectInquiryDraftFields();
  if ($('m_TripType')?.value === 'multi') {
    fields.flight_segments = collectMultiCitySegments();
  }
  if (fields.m_Name?.trim() || fields.m_Phone?.trim() || fields.m_Note?.trim()) {
    saveInquiryDraft(state.agent.id, fields);
  }
}

function showDraftBannerIfAny() {
  const banner = $('draftBanner');
  if (!banner || state.editingId) {
    banner?.classList.add('hidden');
    return;
  }
  const draft = loadInquiryDraft(state.agent?.id);
  banner.classList.toggle('hidden', !draft);
}

function restoreInquiryDraft() {
  const draft = loadInquiryDraft(state.agent?.id);
  if (!draft) return;
  applyInquiryDraftFields(draft);
  if (draft.m_Type) selectType(draft.m_Type);
  onTypeChange();
  onPackageChange();
  if (draft.flight_segments?.length) renderMultiCitySegments(draft.flight_segments);
  onTripTypeChange();
  $('draftBanner')?.classList.add('hidden');
  showToast('Draft restored', 'success');
}

function discardInquiryDraft() {
  clearInquiryDraft(state.agent?.id);
  $('draftBanner')?.classList.add('hidden');
}

function showLogin() {
  clearSessionHint();
  $('loginPage').classList.remove('hidden');
  $('appShell').classList.remove('visible');
  stopAutoRefresh();
}

function canAccessAdmin() {
  return state.agent && (state.agent.role === 'admin' || hasPerm(state.agent, 'manage_agents')
    || hasPerm(state.agent, 'manage_settings') || hasPerm(state.agent, 'delete')
    || hasPerm(state.agent, 'import'));
}

async function loadLauncherProgress() {
  const stat = $('launcherProgressStat');
  const leadsStat = $('launcherLeadsStat');
  if (leadsStat) leadsStat.textContent = 'Browse all inquiries →';
  if (stat && !canViewOwner(state.agent)) {
    stat.textContent = 'Browse your inquiries →';
  }
  try {
    const stats = await api('/api/stats?dept_scope=all');
    const total = stats.total || 0;
    const completed = stats.completed || stats.converted || 0;
    const active = stats.by_bucket?.active || 0;
    const overdue = stats.overdue || 0;
    if (leadsStat) {
      if (overdue) {
        leadsStat.textContent = `${overdue} need attention · ${total} total →`;
      } else {
        leadsStat.textContent = `${total} inquiries · view all →`;
      }
    }
    if (stat && canViewOwner(state.agent)) {
      stat.textContent = `${total} inquiries · ${active} active · ${completed} completed`;
    }
  } catch {
    if (stat && canViewOwner(state.agent)) stat.textContent = 'Open overview →';
  }
}

let launcherSearchGen = 0;

async function runLauncherSearch() {
  const input = $('launcherSearchInput');
  const box = $('launcherSearchResults');
  if (!input || !box) return;
  const q = input.value.trim();
  input.setAttribute('aria-expanded', q.length >= 2 ? 'true' : 'false');
  if (q.length < 2) {
    box.classList.add('hidden');
    box.innerHTML = '';
    return;
  }
  const gen = ++launcherSearchGen;
  box.classList.remove('hidden');
  box.innerHTML = '<div class="launcher-search-status">Searching…</div>';
  try {
    const params = new URLSearchParams({
      q,
      page: '1',
      limit: '8',
      sort: 'updated',
      search_scope: 'global',
    });
    const data = await api('/api/leads?' + params);
    if (gen !== launcherSearchGen) return;
    renderLauncherSearchResults(data.leads || [], q, data.total || 0);
  } catch (e) {
    if (gen !== launcherSearchGen) return;
    box.innerHTML = `<div class="launcher-search-status launcher-search-error">${esc(e.message || 'Search failed')}</div>`;
  }
}

function hideLauncherSearchResults() {
  $('launcherSearchResults')?.classList.add('hidden');
  $('launcherSearchInput')?.setAttribute('aria-expanded', 'false');
}

function renderLauncherSearchResults(leads, q, total) {
  const box = $('launcherSearchResults');
  if (!box) return;
  if (!leads.length) {
    box.innerHTML = `<div class="launcher-search-status">No inquiries match “${esc(q)}”</div>`;
    return;
  }
  state.leads = leads;
  box.innerHTML = leads.map((l) => {
    const sc = STATUS_CFG[l.status] || STATUS_CFG.new;
    const pkg = l.type === 'ticket' ? esc(formatTicketRouteSummary(l)) : esc(l.package_name || '—');
    return `<button type="button" class="launcher-search-hit" data-id="${esc(l.id)}" role="option">
      <span class="launcher-search-hit-main">
        <strong>${esc(l.name)}</strong>
        <span class="type-chip ${esc(l.type)}">${esc(l.type)}</span>
      </span>
      <span class="launcher-search-hit-sub">${esc(l.phone)} · ${pkg}</span>
      <span class="launcher-search-hit-meta">
        <span class="badge ${sc.cls}"><span class="badge-dot"></span>${sc.label}</span>
        <span class="agent-chip"><span class="agent-dot" style="background:${esc(l.agent_color || '#888')}"></span>${esc(l.agent_name || '—')}</span>
        <span class="launcher-search-hit-sub">${relativeTime(l.updated_at)}</span>
      </span>
    </button>`;
  }).join('')
    + (total > leads.length
      ? `<a class="launcher-search-more" href="/leads/?q=${encodeURIComponent(q)}&search_scope=global">View all ${total} results →</a>`
      : '');
  box.querySelectorAll('.launcher-search-hit').forEach((btn) => {
    btn.addEventListener('click', () => {
      openDetail(btn.dataset.id);
      hideLauncherSearchResults();
    });
  });
}

function initLauncher() {
  const input = $('launcherSearchInput');
  const debouncedSearch = debounce(runLauncherSearch, 280);

  input?.addEventListener('input', debouncedSearch);
  input?.addEventListener('focus', () => {
    if ((input.value.trim().length >= 2)) runLauncherSearch();
  });

  $('launcherSearchForm')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const q = input?.value.trim();
    if (!q) {
      input?.focus();
      return;
    }
    location.href = `/leads/?q=${encodeURIComponent(q)}&search_scope=global`;
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.launcher-search-panel')) hideLauncherSearchResults();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.activeElement === input) hideLauncherSearchResults();
  });

  const progressCard = $('launcherProgressCard');
  if (progressCard && !canViewOwner(state.agent)) {
    progressCard.href = '/leads/';
  }
  loadLauncherProgress();
}

function canBulkEditLead(l) {
  if (!state.agent || isViewer(state.agent)) return false;
  return hasPerm(state.agent, 'edit_all') || l.agent_id === state.agent.id || l.has_edit_grant;
}

function shouldSkipQuietDetailRefresh() {
  if (!state.detailId) return true;
  if ($('statusModal')?.classList.contains('open') || state.pendingStatus) return true;
  if ($('callModal')?.classList.contains('open')) return true;
  if ($('historyModal')?.classList.contains('open')) return true;
  if ($('editRequestModal')?.classList.contains('open')) return true;
  if ($('addModal')?.classList.contains('open')) return true;
  if (($('noteInput')?.value || '').trim()) return true;
  const savedFollowup = state.detailLead?.followup_date || '';
  if ($('dFollowup') && $('dFollowup').value !== savedFollowup) return true;
  const savedStatus = state.statusPrev || state.detailLead?.status || '';
  if ($('dStatus') && $('dStatus').value !== savedStatus) return true;
  return false;
}

function showApp() {
  if (isViewer(state.agent)) {
    location.href = '/owner/';
    return;
  }
  document.documentElement.classList.remove('mysky-auth-pending');
  $('loginPage').classList.add('hidden');
  $('appShell').classList.add('visible');
  applyNavAccess(state.agent);
  updateThemeIcon();
  updateThemeMeta();
  updateSoundIcon();
  updateRefreshDot();
  updateReduceEffectsUI();
  updateHeaderGreeting(state.agent);
  checkReminders();
  if (state.agent.must_change_password) showForcePasswordModal();
  else hideForcePasswordModal();
  if (isLauncherPage()) {
    $('launcherAdminLink')?.classList.toggle('hidden', !canAccessAdmin());
    return;
  }
}

function canPickAgent() {
  return state.agent && (state.agent.role === 'admin' || hasPerm(state.agent, 'edit_all'));
}

function detailCanEdit(lead = state.detailLead) {
  if (state.detailAccess?.can_edit === false) return false;
  if (state.detailAccess?.can_edit === true) return true;
  return !!(lead && (hasPerm(state.agent, 'edit_all') || lead.agent_id === state.agent?.id || lead.has_edit_grant));
}

function statusModalOpen() {
  return $('statusModal')?.classList.contains('open');
}

function showStageFormModal(type, status, lead) {
  if (!statusHasStageForm(type, status)) {
    $('statusModal')?.classList.remove('open');
    return;
  }
  const form = renderStageForm(type, status, lead);
  const title = STATUS_CFG[status]?.label || STATUS_LABELS[status] || status;
  if ($('stTitle')) $('stTitle').textContent = state.pendingStatus ? `Next: ${title}` : title;
  const hintEl = $('stHint');
  if (hintEl) {
    hintEl.textContent = form.hint || 'Complete the fields below, then Save.';
    hintEl.classList.toggle('hidden', !form.hint);
  }
  const box = $('stStageFields');
  if (box) {
    box.innerHTML = form.html || '';
    initStageForm(type, status, lead, 'stStageFields');
  }
  if ($('dStageFields')) $('dStageFields').innerHTML = '';
  $('statusModal')?.classList.add('open');
  box?.querySelector('input:not([type="hidden"]), select, textarea')?.focus();
}

function syncTicketIntakeMode() {
  const type = $('m_Type')?.value;
  const isEdit = !!state.editingId;
  const intake = type === 'ticket' && !isEdit;
  $('inquiryModal')?.classList.toggle('ticket-intake-mode', intake);
  $('ticketPoolBanner')?.classList.toggle('hidden', !intake);
  $('ticketIntakeDetailsWrap')?.classList.toggle('hidden', !intake);
  $('ticketPricingSection')?.classList.toggle('hidden', intake);
  $('ticketAdvancedSection')?.classList.toggle('hidden', intake);
  $('perAdtToggleWrap')?.classList.toggle('hidden', intake);
  $('ticketQuoteHint')?.classList.toggle('hidden', type !== 'ticket' || intake);
  $('inquiryTripCard')?.classList.toggle('hidden', intake);
  $('ticketIntakePaxSection')?.classList.toggle('hidden', intake);
  $('inquiryScheduleCard')?.classList.toggle('hidden', intake);
  $('moreToggle')?.classList.toggle('hidden', intake);
  if ($('m_NameLabel')) {
    $('m_NameLabel').textContent = intake ? 'Customer name' : 'Name *';
  }
  if ($('m_Name')) {
    $('m_Name').placeholder = intake ? 'Optional if unknown' : 'Full name';
  }
  if ($('m_SourceLabel')) {
    $('m_SourceLabel').textContent = intake ? 'Source *' : 'Source';
  }
  if ($('inquiryClientTitle')) {
    $('inquiryClientTitle').textContent = intake ? 'Ticket inquiry' : 'Client';
  }
  if ($('modalSub')) {
    $('modalSub').textContent = intake
      ? 'Phone, details, and source — saves to the unassigned pool for quoting.'
      : (isEdit ? 'Update inquiry details' : 'Capture the lead — details come later');
  }
  if ($('agentSelectWrap')) {
    $('agentSelectWrap').classList.toggle('hidden', intake || !canPickAgent());
  }
  if ($('assignedAgentWrap')) {
    $('assignedAgentWrap').classList.toggle('hidden', intake || canPickAgent() || !isEdit);
  }
  if ($('paxCardTitle')) {
    $('paxCardTitle').textContent = intake ? 'Passengers' : 'Passengers & quotation';
  }
}

function syncAgentFieldUI() {
  syncTicketIntakeMode();
  const pick = canPickAgent();
  $('agentSelectWrap')?.classList.toggle('hidden', !pick || ($('m_Type')?.value === 'ticket' && !state.editingId));
  // Only show "assigned to" chip when editing as a non-admin
  $('assignedAgentWrap')?.classList.toggle('hidden', pick || !state.editingId);
  if (!pick && state.agent) {
    const pill = $('assignedAgentPill');
    if (pill) {
      const ag = state.agents.find((a) => a.id === (state.detailLead?.agent_id || state.agent.id)) || state.agent;
      pill.innerHTML = `<span class="agent-dot" style="background:${esc(ag.color)}"></span>${esc(ag.name)}`;
    }
  }
}

function toggleSound() {
  state.soundOn = !state.soundOn;
  localStorage.setItem('mysky_sound', state.soundOn ? '1' : '0');
  updateSoundIcon();
  showToast(state.soundOn ? 'Reminder sound on' : 'Reminder sound muted', state.soundOn ? 'success' : 'warning');
  if (state.soundOn) playReminderSound();
}

function updateSoundIcon() {
  const btn = $('soundToggle');
  if (!btn) return;
  setIcon(btn, state.soundOn ? 'volume-up' : 'volume-mute');
  btn.classList.toggle('is-on', state.soundOn);
  btn.setAttribute('aria-pressed', state.soundOn ? 'true' : 'false');
}

function startAutoRefresh() {
  stopAutoRefresh();
  if (!state.autoRefresh) return;
  state.refreshTimer = setInterval(() => {
    if (document.visibilityState === 'visible' && state.agent) refresh({ silent: true });
  }, 60000);
}

function stopAutoRefresh() {
  if (state.refreshTimer) clearInterval(state.refreshTimer);
  state.refreshTimer = null;
}

function toggleAutoRefresh() {
  state.autoRefresh = !state.autoRefresh;
  localStorage.setItem('mysky_autorefresh', state.autoRefresh ? '1' : '0');
  updateRefreshDot();
  if (state.autoRefresh) startAutoRefresh();
  else stopAutoRefresh();
}

function updateRefreshDot() {
  const dot = $('refreshDot');
  const btn = $('refreshToggle');
  if (dot) dot.classList.toggle('off', !state.autoRefresh);
  if (btn) {
    btn.classList.toggle('is-on', state.autoRefresh);
    btn.setAttribute('aria-pressed', state.autoRefresh ? 'true' : 'false');
  }
}

function updateReduceEffectsUI() {
  const on = isLiteModeOn();
  const btn = $('reduceEffectsToggle') || $('liteModeToggle');
  if (!btn) return;
  btn.classList.toggle('is-on', on);
  btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  const staging = document.documentElement.classList.contains('env-staging');
  btn.title = on
    ? 'Fast mode ON — tap for full visuals'
    : (staging ? 'Fast mode OFF — tap for glass effects (slower)' : 'Fast mode OFF — tap for full visuals');
  btn.setAttribute('aria-label', on ? 'Disable fast mode' : 'Enable fast mode');
  setIcon(btn, on ? 'circle-check' : 'bolt');
}

function toggleReduceEffectsHandler() {
  const on = toggleReduceEffects();
  updateReduceEffectsUI();
  showToast(on ? 'Fast mode on — smoother layout, less memory' : 'Full visuals on — refresh the page to apply', on ? 'success' : 'warning');
}

function toggleSidebar(force) {
  const sb = $('sidebar');
  const bd = $('sidebarBackdrop');
  if (!sb) return;
  const open = force === undefined ? !sb.classList.contains('open') : !!force;
  sb.classList.toggle('open', open);
  bd?.classList.toggle('open', open);
}

function closeModal(id) {
  if (id === 'addModal') {
    maybeSaveInquiryDraft();
    state.editingId = null;
  }
  $(id)?.classList.remove('open');
}

async function onLogin(e) {
  e.preventDefault();
  const err = $('loginError');
  err.classList.add('hidden');
  try {
    const data = await api('/api/login', {
      method: 'POST',
      body: {
        username: $('loginUsername').value.trim(),
        password: $('loginPassword').value,
      },
    });
    state.agent = data.agent;
    resetSessionExpiredFlag();
    showApp();
    await loadMeta();
    if (isLauncherPage()) {
      initLauncher();
      loadAccessInbox();
    }
    startAutoRefresh();
    showToast('Welcome, ' + state.agent.name, 'success');
  } catch (ex) {
    err.textContent = ex.message;
    err.classList.remove('hidden');
  }
}

async function logout() {
  try {
    await api('/api/logout', { method: 'POST' });
  } catch { /* clear local session anyway */ }
  clearSessionHint();
  state.agent = null;
  hideForcePasswordModal();
  showLogin();
}

async function loadMeta() {
  try {
    const [agentsRes, settingsRes] = await Promise.all([
      api('/api/agents'),
      api('/api/settings'),
    ]);
    state.agents = agentsRes.agents || [];
    state.settings = settingsRes.settings || state.settings;
    if (state.settings.status_pipelines) applyPipelineSettings(state.settings.status_pipelines);
    fillAgentFilters();
  } catch (e) {
    showToast(e.message || 'Could not load settings', 'error');
  }
}

function fillAgentFilters() {
  const sel = $('fAgent');
  if (!sel) return;
  const cur = sel.value;
  sel.innerHTML = '<option value="">All Agents</option>' +
    state.agents.map((a) => `<option value="${esc(a.id)}">${esc(a.name)}</option>`).join('');
  sel.value = cur;
}

function syncSidebarActive() {
  document.querySelectorAll('[data-filter]').forEach((x) => x.classList.remove('active'));
  const f = state.filter;
  let key = 'all';
  if (f.overdue) key = 'overdue';
  else if (f.custom) key = 'custom';
  else if (f.agent_id) key = `agent:${f.agent_id}`;
  else if (f.bucket) key = `bucket:${f.bucket}`;
  else if (f.status) key = f.status;
  else if (f.type) key = f.type;
  const btn = document.querySelector(`[data-filter="${key}"]`);
  if (btn) btn.classList.add('active');
}

function applySidebarFilter(f) {
  state.filter = {
    type: '', status: '', bucket: '', overdue: false, custom: false, agent_id: '',
    q: state.filter.q, dept_scope: state.filter.dept_scope || 'my',
  };
  if (f === 'tour' || f === 'visa' || f === 'ticket') {
    state.filter.type = f;
  } else if (f === 'overdue') state.filter.overdue = true;
  else if (f === 'custom') state.filter.custom = true;
  else if (f.startsWith('bucket:')) {
    state.filter.bucket = f.slice(7);
  } else if (STATUS_ORDER.includes(f) || f === 'client_not_interested') {
    state.filter.status = f;
  } else if (f.startsWith('agent:')) {
    state.filter.agent_id = f.slice(6);
  }
  syncToolbarFromFilter();
  state.page = 1;
  syncPills();
  syncSidebarActive();
  applyDepartmentUI();
  persistFilter();
  refreshListOnly();
}

function syncFilterFromToolbar() {
  state.filter.type = $('fType')?.value || '';
  state.filter.status = $('fStatus')?.value || '';
  state.filter.agent_id = $('fAgent')?.value || '';
  state.filter.bucket = '';
  state.filter.overdue = false;
  state.filter.custom = false;
  state.page = 1;
  populateStatusSelects();
  syncPills();
  syncSidebarActive();
  persistFilter();
  refreshListOnly();
}

function syncToolbarFromFilter() {
  if ($('fType')) $('fType').value = state.filter.type || '';
  if ($('fStatus')) $('fStatus').value = state.filter.status || '';
  if ($('fAgent')) $('fAgent').value = state.filter.agent_id || '';
}

function buildLeadsExplorerUrl(extra = {}) {
  const params = new URLSearchParams();
  if (state.filter.type) params.set('type', state.filter.type);
  if (state.filter.status) params.set('status', state.filter.status);
  if (state.filter.bucket) params.set('bucket', state.filter.bucket);
  if (state.filter.agent_id) params.set('agent_id', state.filter.agent_id);
  if (state.filter.overdue) params.set('overdue', '1');
  if (state.filter.custom) params.set('custom', '1');
  const q = state.filter.q || $('searchInput')?.value?.trim();
  if (q) params.set('q', q);
  if (effectiveDeptScope() !== 'all') params.set('dept_scope', effectiveDeptScope());
  Object.entries(extra).forEach(([k, v]) => {
    if (v == null || v === '') params.delete(k);
    else params.set(k, String(v));
  });
  const qs = params.toString();
  return qs ? `/leads/?${qs}` : '/leads/';
}

function updateViewAllLink() {
  const btn = $('viewAllLeadsBtn');
  if (!btn) return;
  btn.href = buildLeadsExplorerUrl();
}

function buildListParams() {
  const q = $('searchInput')?.value || '';
  state.filter.q = q;
  const params = new URLSearchParams({ page: '1', limit: String(HOME_LIST_LIMIT) });
  if (state.filter.type) params.set('type', state.filter.type);
  if (state.filter.status) params.set('status', state.filter.status);
  if (state.filter.bucket) params.set('bucket', state.filter.bucket);
  if (state.filter.agent_id) params.set('agent_id', state.filter.agent_id);
  if (state.filter.q) params.set('q', state.filter.q);
  if (state.filter.overdue) params.set('overdue', '1');
  if (state.filter.custom) params.set('custom', '1');
  params.set('dept_scope', effectiveDeptScope());
  return params;
}

function applyListResult(list, opts = {}) {
  state.leads = list.leads || [];
  state.page = list.page;
  state.total_pages = list.total_pages;
  state.total = list.total;
  pruneSelection();
  renderTable();
  updateBulkBar();
  syncPills();
  syncSidebarActive();
  renderFilterChips(state.filter, state.agents, onFilterChipsChange);
  updateViewAllLink();
  const synced = $('lastSynced');
  if (synced) {
    synced.textContent = opts.silent
      ? `Synced ${new Date().toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}`
      : 'Ready';
  }
}

function applyStatsResult(stats) {
  renderStats(stats);
  renderClickablePipeline(stats, applySidebarFilter);
  renderSidebarCounts(stats);
}

async function refreshListOnly(opts = {}) {
  if (hostListRefresh) return hostListRefresh(opts);
  if (isLauncherPage()) return;
  const gen = ++state.listFetchGen;
  setListLoading(true);
  try {
    const list = await api('/api/leads?' + buildListParams());
    if (gen !== state.listFetchGen) return;
    applyListResult(list, opts);
  } catch (e) {
    if (gen !== state.listFetchGen) return;
    if (!opts.silent) showToast(e.message || 'Refresh failed', 'error');
  } finally {
    if (gen === state.listFetchGen) setListLoading(false);
  }
}

async function refreshStatsOnly() {
  if (hostStatsRefresh) return hostStatsRefresh();
  if (isLauncherPage()) return;
  if (!canViewOwner(state.agent)) {
    $('statsSection')?.classList.add('hidden');
    $('pipelineWrap')?.classList.add('hidden');
    return;
  }
  const gen = ++state.statsFetchGen;
  try {
    const params = new URLSearchParams();
    params.set('dept_scope', effectiveDeptScope());
    const stats = await api('/api/stats?' + params);
    if (gen !== state.statsFetchGen) return;
    applyStatsResult(stats);
  } catch { /* stats optional */ }
}

async function refresh(opts = {}) {
  if (isLauncherPage()) {
    if (opts.silent && state.detailId && !opts.skipDetail && !shouldSkipQuietDetailRefresh()) {
      try { await reloadDetailQuiet(state.detailId); } catch { /* ignore */ }
    } else {
      loadLauncherProgress();
    }
    return;
  }
  await Promise.all([
    refreshListOnly(opts),
    refreshStatsOnly(),
  ]);

  if (opts.silent && state.detailId && !opts.skipDetail && !shouldSkipQuietDetailRefresh()) {
    try { await reloadDetailQuiet(state.detailId); } catch { /* ignore */ }
  }
}

/** Lightweight refresh after a single-lead mutation — avoids duplicate detail fetches. */
async function refreshAfterLeadChange(opts = {}) {
  const {
    detailId = state.detailId,
    reloadDetail = true,
    reloadStats = false,
  } = opts;
  const jobs = [refreshListOnly({ silent: true })];
  if (reloadStats) jobs.push(refreshStatsOnly());
  if (reloadDetail && detailId) jobs.push(reloadDetailQuiet(detailId));
  await Promise.all(jobs);
}

function pruneSelection() {
  const visible = new Set(state.leads.map((l) => l.id));
  for (const id of [...state.selected]) {
    if (!visible.has(id)) state.selected.delete(id);
  }
}

function activitySortKey(item) {
  if (item.kind === 'note') {
    const t = new Date(item.time).getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  const parts = String(item.time || '').trim().split(/\s+/);
  const d = parts[0];
  const tm = parts[1] || '00:00';
  const t = new Date(`${d}T${tm}:00+05:30`).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function renderStats(s) {
  $('s-total').textContent = s.total;
  $('s-tours').textContent = s.by_type?.tour || 0;
  $('s-visas').textContent = s.by_type?.visa || 0;
  $('s-tickets').textContent = s.by_type?.ticket || 0;
  $('s-followup').textContent = s.by_status?.followup || 0;
  $('s-converted').textContent = s.converted;
  $('s-rate').textContent = `${s.conversion_rate}% rate`;
  $('s-overdue').textContent = s.overdue;
}

function renderPipeline(s) {
  renderClickablePipeline(s, applySidebarFilter);
}

function quickFilter(f) {
  document.querySelectorAll('[data-filter]').forEach((x) => x.classList.remove('active'));
  const btn = document.querySelector(`[data-filter="${f}"]`);
  if (btn) btn.classList.add('active');
  applySidebarFilter(f);
  toggleSidebar(false);
}

function pillFilter(type) {
  document.querySelectorAll('[data-filter]').forEach((x) => x.classList.remove('active'));
  state.filter.type = type === 'all' ? '' : type;
  state.filter.status = '';
  state.filter.bucket = '';
  state.filter.agent_id = '';
  state.filter.overdue = false;
  state.filter.custom = false;
  syncToolbarFromFilter();
  state.page = 1;
  syncPills();
  syncSidebarActive();
  persistFilter();
  refreshListOnly();
}

function syncPills() {
  const t = state.filter.type || $('fType')?.value || 'all';
  document.querySelectorAll('[data-pill]').forEach((p) => {
    p.classList.toggle('active', p.dataset.pill === (t || 'all'));
  });
}

function renderSidebarCounts(s) {
  const set = (id, v) => { const el = $(id); if (el) el.textContent = v; };
  set('c-all', s.total);
  set('c-tour', s.by_type?.tour || 0);
  set('c-visa', s.by_type?.visa || 0);
  set('c-ticket', s.by_type?.ticket || 0);
  set('c-overdue', s.overdue || 0);
  const buckets = s.by_bucket || {};
  set('c-bucket-active', buckets.active || 0);
  set('c-bucket-waiting_client', buckets.waiting_client || 0);
  set('c-bucket-waiting_supplier', buckets.waiting_supplier || 0);
  set('c-bucket-completed', buckets.completed || 0);
  set('c-bucket-closed', buckets.closed || 0);
  set('c-client_not_interested', s.by_status?.client_not_interested || 0);
  const agentBox = $('agentFilters');
  if (agentBox && state.agents.length) {
    agentBox.innerHTML = (s.by_agent || []).map((a) =>
      `<button class="sidebar-item" data-filter="agent:${esc(a.id)}">
        <span class="agent-dot" style="background:${esc(a.color)}"></span> ${esc(a.name)}
        <span class="sidebar-count">${a.c}</span>
      </button>`
    ).join('');
    agentBox.querySelectorAll('[data-filter]').forEach((el) => {
      el.addEventListener('click', () => {
        document.querySelectorAll('[data-filter]').forEach((x) => x.classList.remove('active'));
        el.classList.add('active');
        applySidebarFilter(el.dataset.filter);
      });
    });
    if (state.filter.agent_id) {
      const active = agentBox.querySelector(`[data-filter="agent:${state.filter.agent_id}"]`);
      if (active) active.classList.add('active');
    }
  }
}

function bindTableEvents() {
  const tbody = $('leadsBody');
  if (!tbody || tbody.dataset.bound) return;
  tbody.dataset.bound = '1';

  tbody.addEventListener('click', (e) => {
    const advBtn = e.target.closest('[data-advance]');
    if (advBtn) {
      e.stopPropagation();
      quickAdvanceLead(advBtn.dataset.advance, advBtn.dataset.next);
      return;
    }
    const waBtn = e.target.closest('[data-wa]');
    if (waBtn) {
      e.stopPropagation();
      const url = whatsappUrl(waBtn.dataset.wa);
      if (url) window.open(url, '_blank');
      return;
    }
    const editBtn = e.target.closest('[data-edit]');
    if (editBtn) {
      e.stopPropagation();
      openEditModal(editBtn.dataset.edit);
      return;
    }
    if (e.target.closest('[data-check]') || e.target.closest('.row-actions')) return;
    const row = e.target.closest('tr[data-id]');
    if (row) openDetail(row.dataset.id);
  });

  tbody.addEventListener('change', (e) => {
    const cb = e.target.closest('[data-check]');
    if (!cb) return;
    const id = cb.dataset.check;
    if (cb.checked) state.selected.add(id);
    else state.selected.delete(id);
    updateBulkBar();
  });

  tbody.addEventListener('mouseenter', (e) => {
    const row = e.target.closest('tr[data-id]');
    if (row) leadPrefetch.schedule(row.dataset.id);
  }, true);
}

function renderTable() {
  if (hostRenderList) { hostRenderList(); return; }
  const tbody = $('leadsBody');
  if (!tbody) return;
  const empty = $('emptyState');
  if (!state.leads.length) {
    tbody.innerHTML = '';
    empty.classList.remove('hidden');
    $('pageInfo').textContent = 'No results';
    updateViewAllLink();
    return;
  }
  empty.classList.add('hidden');
  const reduceFx = isLiteModeOn()
    || document.documentElement.classList.contains('reduce-effects')
    || document.documentElement.classList.contains('balanced-ui');
  tbody.innerHTML = state.leads.map((l, idx) => {
    const sc = STATUS_CFG[l.status] || STATUS_CFG.new;
    const od = isOverdue(l);
    const checked = state.selected.has(l.id) ? 'checked' : '';
    const selected = state.selected.has(l.id) ? 'selected-row' : '';
    const focused = idx === state.focusedRowIdx ? 'row-focused' : '';
    const canBulk = canBulkEditLead(l);
    const canEdit = hasPerm(state.agent, 'edit_all') || l.agent_id === state.agent.id || l.has_edit_grant;
    const nextSt = canEdit ? getNextStatus(l.type, l.status) : null;
    const nextLabel = nextSt ? (STATUS_CFG[nextSt]?.label || STATUS_LABELS[nextSt] || nextSt) : '';
    const dueBal = leadOutstandingBalance(l);
    const expired = isBookingExpired(l);
    const quoteCell = fmtAmt(l.type === 'ticket' ? (ticketQuotedTotal(l) ?? l.amount) : l.amount)
      + (dueBal ? `<div class="balance-due-tag">Due ${fmtAmt(dueBal)}</div>` : '');
    const pkg = l.type === 'ticket'
      ? formatTicketRouteSummary(l)
      : esc(l.package_name || '—');
    return `<tr data-id="${esc(l.id)}" class="${selected} ${focused}${dueBal ? ' row-balance-due' : ''}${expired ? ' row-booking-expired' : ''}">
      <td><input type="checkbox" ${checked}${canBulk ? '' : ' disabled'} data-check="${esc(l.id)}" aria-label="Select ${esc(l.name)}"></td>
      <td><div class="client-cell">
        <div class="avatar" style="background:${esc(getAvatarColor(l.name))}">${esc(getInitials(l.name))}</div>
        <div><div class="client-name">${esc(l.name)}${l.is_custom ? '<span class="custom-star">★</span>' : ''}</div>
        <div class="client-phone">${esc(l.phone)}</div></div>
      </div></td>
      <td><span class="type-tag"><span class="type-chip ${esc(l.type)}">${esc(l.type)}</span> ${pkg}</span></td>
      <td><span class="agent-chip"><span class="agent-dot" style="background:${esc(l.agent_color || '#888')}"></span>${esc(l.agent_name || '—')}</span></td>
      <td><span class="badge ${sc.cls}"><span class="badge-dot"></span>${sc.label}${expired ? '<span class="booking-expired-tag">Expired</span>' : ''}</span></td>
      <td class="${od ? 'overdue' : ''}${od && !reduceFx ? ' overdue-pulse' : ''}">${fmtDate(l.followup_date)}</td>
      <td class="${dueBal ? 'quote-cell-due' : ''}">${quoteCell}</td>
      <td class="update-cell">
        <div class="update-by">${esc(l.last_edited_by_name || '—')}</div>
        <div class="update-when">${relativeTime(l.updated_at)}</div>
      </td>
      <td><div class="row-actions">
        ${nextSt ? `<button class="row-btn row-advance" data-advance="${esc(l.id)}" data-next="${esc(nextSt)}" title="Advance to ${esc(nextLabel)}">${icon('chevron-down')}</button>` : ''}
        <button class="row-btn wa-btn" data-wa="${esc(l.phone)}" title="WhatsApp">${icon('whatsapp')}</button>
        ${canEdit ? `<button class="row-btn" data-edit="${esc(l.id)}" title="Edit">${icon('pen')}</button>` : ''}
      </div></td>
    </tr>`;
  }).join('');

  if (state.total <= HOME_LIST_LIMIT) {
    $('pageInfo').textContent = `${state.total} ${state.total === 1 ? 'inquiry' : 'inquiries'}`;
  } else {
    $('pageInfo').textContent = `Showing ${state.leads.length} of ${state.total} inquiries`;
  }
  updateViewAllLink();
  const selectAll = $('selectAllLeads');
  if (selectAll) {
    const pageIds = state.leads.filter(canBulkEditLead).map((l) => l.id);
    selectAll.checked = pageIds.length > 0 && pageIds.every((id) => state.selected.has(id));
    selectAll.indeterminate = !selectAll.checked && pageIds.some((id) => state.selected.has(id));
  }
}

function updateBulkBar() {
  const bar = $('bulkBar');
  const n = state.selected.size;
  bar.classList.toggle('visible', n > 0);
  const onPage = state.leads.filter((l) => state.selected.has(l.id)).length;
  const offPage = n - onPage;
  $('bulkCount').textContent = offPage > 0 ? `${n} (${offPage} off-page)` : String(n);
}

function applyDetailPanel(data, id) {
  const l = data.lead;
  const access = data.access || state.detailAccess;
  const poolIntake = isUnassignedPoolTicket(l) && isTicketQuickIntake(l);
  const intakeHero = isTicketQuickIntake(l) && l.status === 'new';

  $('detailPanel')?.classList.toggle('detail-pool-intake', poolIntake);
  $('detailPanel')?.classList.toggle('detail-intake-layout', intakeHero);

  if (intakeHero) {
    $('dIntakeHero')?.classList.remove('hidden');
    $('dStandardHero')?.classList.add('hidden');
    if ($('dIntakePhone')) $('dIntakePhone').textContent = l.phone || '—';
    if ($('dPhone')) $('dPhone').textContent = l.phone || '—';
    const showName = !isAutoTicketCustomerName(l.name, l.phone);
    const nameEl = $('dIntakeCustomerName');
    if (nameEl) {
      nameEl.textContent = showName ? l.name : '';
      nameEl.classList.toggle('hidden', !showName);
    }
    if ($('dName')) $('dName').textContent = showName ? l.name : (l.phone || '—');
  } else {
    $('dIntakeHero')?.classList.add('hidden');
    $('dStandardHero')?.classList.remove('hidden');
    $('dName').textContent = l.name;
    $('dPhone').textContent = l.phone;
  }

  const typeChip = $('dTypeChip');
  if (typeChip) {
    typeChip.textContent = l.type;
    typeChip.className = `type-chip ${l.type}`;
  }

  const sc = STATUS_CFG[l.status] || STATUS_CFG.new;
  const badge = $('dStatusBadge');
  const customStatus = detailTicketStatusLabel(l);
  if (badge) {
    badge.className = `badge ${customStatus ? (l.agent_id ? 'badge-contacted' : 'badge-new') : sc.cls}`;
    $('dStatusLabel').textContent = customStatus || sc.label;
  }

  let pkgLine = l.type === 'ticket'
    ? (isTicketQuickIntake(l)
      ? (ticketIntakeText(l) || formatTicketRouteSummary(l))
      : formatTicketRouteSummary(l) + (l.trip_type && l.trip_type !== 'multi' ? ` · ${l.trip_type.replace('_', ' ')}` : ''))
    : (l.package_name || '—');
  const indiaLine = formatIndiaEvisa(l.extra);
  if (indiaLine) pkgLine += ` · ${indiaLine}`;
  const paxStr = formatPax(l.extra);
  if (paxStr) pkgLine += ` · ${paxStr}`;
  const rates = l.extra?.pax_rates;
  if (rates && (rates.adt || rates.chd || rates.inf)) {
    const rp = [];
    if (rates.adt) rp.push(`ADT ${fmtAmt(rates.adt)}`);
    if (rates.chd) rp.push(`CHD ${fmtAmt(rates.chd)}`);
    if (rates.inf) rp.push(`INF ${fmtAmt(rates.inf)}`);
    if (rp.length) pkgLine += ` · ${rp.join(' / ')}`;
  }
  if (l.extra?.tour_duration) pkgLine += ` · ${l.extra.tour_duration}`;
  if (l.type === 'ticket') {
    const prefParts = [l.airline, l.ticket_class].filter(Boolean);
    const morePrefs = formatTicketPreferences(l.extra);
    if (morePrefs) prefParts.push(morePrefs);
    if (prefParts.length) pkgLine += ` · ${prefParts.join(' · ')}`;
  }
  if (l.is_custom && l.custom_req && !isTicketQuickIntake(l)) pkgLine += ` · Custom: ${l.custom_req}`;
  if (!intakeHero) {
    $('dPackageLine')?.classList.remove('hidden');
    $('dPackageLine').textContent = pkgLine;
  } else if ($('dPackageLine')) {
    $('dPackageLine').textContent = '';
    $('dPackageLine').classList.add('hidden');
  }

  $('dAgent').textContent = l.agent_name || (l.agent_id == null && l.type === 'ticket' ? 'Unassigned' : '—');
  $('dSource').textContent = l.source || '—';
  $('dInquiry').textContent = fmtDate(l.inquiry_date);
  $('dStatus').value = l.status;
  populateDetailStatusSelect(l);
  updateDetailStatusHighlight(l.status);
  $('dFollowup').value = l.followup_date || '';
  $('dEditedBy').textContent = `${l.last_edited_by_name || '—'} · ${relativeTime(l.updated_at)}`;

  const quote = l.type === 'ticket' ? ticketQuotedTotal(l) : l.amount;
  $('dAmount').textContent = fmtAmt(quote);
  const advance = l.extra?.advance_paid;
  const balance = l.extra?.balance_due;
  $('dAdvanceCell')?.classList.toggle('hidden', advance == null);
  $('dBalanceCell')?.classList.toggle('hidden', balance == null);
  if ($('dAdvance')) $('dAdvance').textContent = fmtAmt(advance);
  if ($('dBalance')) {
    $('dBalance').textContent = fmtAmt(balance);
    $('dBalance').style.color = balance > 0 ? 'var(--orange)' : 'var(--green)';
  }
  $('dCost').textContent = fmtAmt(effectiveCostAmount(l));
  const soldTotal = effectiveSoldAmount(l);
  $('dSold').textContent = fmtAmt(soldTotal);
  const profit = effectiveProfit(l);
  const profitEl = $('dProfit');
  if (profitEl) {
    profitEl.textContent = fmtAmt(profit);
    profitEl.style.color = profit == null ? '' : (profit >= 0 ? 'var(--green)' : 'var(--red)');
  }

  const hasMoney = quote != null || effectiveCostAmount(l) != null || l.sold_amount != null
    || advance != null || balance != null;
  $('dMoneyStrip')?.classList.toggle('hidden', !hasMoney);

  $('dTravel').textContent = l.type === 'ticket'
    ? (isTicketQuickIntake(l) ? (ticketIntakeText(l) || formatTicketTravelLine(l)) : formatTicketTravelLine(l))
    : `${fmtDate(l.depart_date)}${l.return_date ? ' → ' + fmtDate(l.return_date) : ''}`;
  $('dAirline').textContent = l.airline || '—';
  $('dPnr').textContent = l.pnr || '—';
  const hideFlightSidebar = l.type === 'ticket' && intakeHero && !l.origin && !l.destination && !l.airline && !l.pnr;
  $('ticketFields')?.classList.toggle('hidden', l.type !== 'ticket' || hideFlightSidebar);

  loadLeadReminders(id);

  if (data.duplicates?.length) {
    renderDupWarn(data.duplicates, id);
  } else {
    $('dupWarn')?.classList.add('hidden');
    if ($('dupWarn')) $('dupWarn').innerHTML = '';
  }

  updateDetailBalanceWarn(l);
  updateBookingExpiryCountdown(l);

  updateDetailSidebarTitle(l.type);
  updateDetailAccessUI(l, access);
  renderDetailStagePanel();
}

function isDetailPoolIntakeView(lead = state.detailLead) {
  return !!(lead && isUnassignedPoolTicket(lead) && isTicketQuickIntake(lead));
}

function updateDetailSidebarTitle(type) {
  const el = $('dSidebarTitle');
  if (!el) return;
  const labels = { tour: 'Tour overview', ticket: 'Ticket overview', visa: 'Visa overview' };
  el.textContent = labels[type] || 'Overview';
}

async function openDetail(id) {
  const reqId = id;
  state.detailId = id;
  state.detailStageTab = null;
  state.focusedRowIdx = state.leads.findIndex((l) => l.id === id);

  const cached = state.leads.find((l) => l.id === id);
  const warmed = leadPrefetch.get(id);
  if (cached) {
    $('detailOverlay').classList.add('open');
    $('detailPanel').classList.add('open');
    applyDetailPanel(warmed || { lead: cached, notes: [], call_logs: [] }, id);
    const timeline = $('activityTimeline');
    if (timeline && !warmed) {
      timeline.innerHTML = '<div class="activity-empty">Loading activity…</div>';
    }
  }

  try {
    const data = await api('/api/leads/' + id);
    state.leadPrefetchCache.set(id, data);
    if (state.detailId !== reqId) return;
    const l = data.lead;
    state.detailLead = l;
    state.detailHistory = {
      lead: l,
      notes: data.notes || [],
      call_logs: data.call_logs || [],
      audit_events: data.audit_events || [],
    };
    state.statusPrev = l.status;

    applyDetailPanel(data, id);

    if (!cached) {
      $('detailOverlay').classList.add('open');
      $('detailPanel').classList.add('open');
    }
  } catch (e) {
    if (!cached) {
      showToast(e.message || 'Could not load inquiry', 'error');
      state.detailId = null;
      return;
    }
    showToast(e.message || 'Could not refresh inquiry details', 'warning');
  }
}

function toggleDetailSidebar(force) {
  const panel = $('detailPanel');
  const btn = $('dOverviewToggle');
  if (!panel) return;
  const open = typeof force === 'boolean' ? force : !panel.classList.contains('detail-rail-open');
  panel.classList.toggle('detail-rail-open', open);
  panel.classList.toggle('detail-sidebar-open', open);
  if (btn) {
    btn.setAttribute('aria-pressed', open ? 'true' : 'false');
    btn.classList.toggle('active', open);
    btn.setAttribute('aria-label', open ? 'Hide summary panel' : 'Show summary panel');
  }
}

function closeDetailPopovers() {
  $('dReminderPopover')?.classList.add('hidden');
  $('dOverflowMenu')?.classList.add('hidden');
  $('dManualStatusPanel')?.classList.add('hidden');
  $('dReminderBtn')?.setAttribute('aria-expanded', 'false');
  $('dOverflowBtn')?.setAttribute('aria-expanded', 'false');
}

function closeDetail() {
  closeDetailPopovers();
  const panel = $('detailPanel');
  $('detailOverlay').classList.remove('open');
  panel?.classList.remove('open', 'detail-rail-open', 'detail-sidebar-open');
  const btn = $('dOverviewToggle');
  if (btn) {
    btn.setAttribute('aria-pressed', 'false');
    btn.classList.remove('active');
  }
  state.detailId = null;
  state.detailLead = null;
  state.detailAccess = null;
  state.detailHistory = null;
  state.historyItems = [];
  state.pendingStatus = null;
  state.stagePanelUrgent = false;
  state.detailStageTab = null;
  if ($('dStageFields')) $('dStageFields').innerHTML = '';
  $('dStagePanel')?.classList.remove('detail-stage-urgent', 'detail-balance-due');
}

function updateDetailAccessUI(lead, access) {
  state.detailAccess = access || {};
  const canEdit = !!access?.can_edit;
  const canComment = access?.can_comment !== false;
  const isOwner = !!access?.is_owner;
  const pending = access?.pending_request;

  if ($('dStatus')) $('dStatus').disabled = !canEdit;
  if ($('dFollowup')) $('dFollowup').disabled = !canEdit;
  if ($('dFollowupSave')) $('dFollowupSave').disabled = !canEdit;
  document.querySelector('.status-workflow-panel')?.classList.toggle('read-only', !canEdit);
  $('reminderFormFields')?.classList.toggle('hidden', !canEdit);
  document.querySelector('.detail-note-row')?.classList.toggle('hidden', !canComment);
  $('dEditBtn')?.classList.toggle('hidden', !canEdit);
  $('dMobileEdit')?.classList.toggle('hidden', !canEdit);
  $('dRequestBtn')?.classList.toggle('hidden', canEdit || !canComment || isOwner || !!pending);
  $('dTakeoverBtn')?.classList.toggle('hidden', !access?.can_takeover);
  $('dClaimBanner')?.classList.toggle('hidden', !access?.can_claim);
  $('deleteBtn')?.classList.toggle('hidden', !canDeleteLead(state.agent, lead, access));
  $('dLogCallBtn')?.classList.toggle('hidden', !canComment);
  $('dMobileCallBtn')?.classList.toggle('hidden', !canComment);

  const bar = $('dAccessBar');
  if (bar) {
    if (!canEdit && canComment && !isOwner) {
      bar.classList.remove('hidden');
      if (pending) {
        bar.textContent = 'Edit request pending — you can still log calls and add notes until approved.';
      } else {
        bar.textContent = 'View only on this inquiry — log calls and add notes, or request edit / take over below.';
      }
    } else if (canEdit && access?.has_edit_grant) {
      bar.classList.remove('hidden');
      bar.textContent = 'You have granted edit access on this inquiry (not the assigned agent).';
    } else {
      bar.classList.add('hidden');
      bar.textContent = '';
    }
  }
}

async function reloadDetailQuiet(id) {
  if (!state.detailId || state.detailId !== id) return;
  const data = await api('/api/leads/' + id);
  state.detailLead = data.lead;
  state.detailHistory = {
    lead: data.lead,
    notes: data.notes || [],
    call_logs: data.call_logs || [],
    audit_events: data.audit_events || [],
  };
  state.statusPrev = data.lead.status;
  applyDetailPanel(data, id);
}

async function requestEditAccess() {
  if (!state.detailId || state.detailAccess?.can_edit) return;
  $('editRequestMessage').value = '';
  $('editRequestModal')?.classList.add('open');
  $('editRequestMessage')?.focus();
}

async function submitEditRequest() {
  if (!state.detailId || state.detailAccess?.can_edit) return;
  const message = ($('editRequestMessage')?.value || '').trim();
  try {
    await api('/api/leads/' + state.detailId + '/request-edit', {
      method: 'POST',
      body: { message },
    });
    $('editRequestModal')?.classList.remove('open');
    showToast('Edit request sent to the assigned agent', 'success');
    await openDetail(state.detailId);
    loadAccessInbox();
  } catch (e) {
    showToast(e.message || 'Could not send request', 'error');
  }
}

async function takeOverLead() {
  if (!state.detailId || state.detailAccess?.can_edit || state.detailAccess?.is_owner) return;
  const owner = $('dAgent')?.textContent?.trim() || 'the assigned agent';
  if (!confirm(`Take over this inquiry from ${owner}? You will become the assigned agent.`)) return;
  try {
    await api('/api/leads/' + state.detailId + '/takeover', {
      method: 'POST',
      body: { confirm: true },
    });
    showToast('Inquiry assigned to you', 'success');
    await refresh();
    await openDetail(state.detailId);
    loadAccessInbox();
  } catch (e) {
    showToast(e.message || 'Take over failed', 'error');
  }
}

async function claimLead() {
  if (!state.detailId || !state.detailAccess?.can_claim) return;
  if (!confirm('Take over this inquiry from the unassigned pool? You will become the assigned agent.')) return;
  try {
    await api('/api/leads/' + state.detailId + '/claim', { method: 'POST', body: {} });
    showToast('Inquiry assigned to you', 'success');
    await refresh();
    await openDetail(state.detailId);
  } catch (e) {
    showToast(e.message || 'Could not claim inquiry', 'error');
  }
}

export async function loadAccessInbox() {
  const box = $('accessInbox');
  if (!box || !state.agent) return;
  try {
    const data = await api('/api/lead-access/inbox');
    const reqs = data.requests || [];
    if (!reqs.length) {
      box.classList.add('hidden');
      box.innerHTML = '';
      return;
    }
    box.classList.remove('hidden');
    box.innerHTML = `<div style="font-weight:600;margin-bottom:8px">${icon('inbox')} Edit access requests</div>`
      + reqs.map((r) => `
        <div class="access-inbox-item">
          <div>
            <strong>${esc(r.requester_name || 'Agent')}</strong> wants to edit
            <strong>${esc(r.lead_name || 'inquiry')}</strong>
            ${r.message ? `<div style="color:var(--fg-dim);margin-top:2px">${esc(r.message)}</div>` : ''}
          </div>
          <div class="btn-bar-wrap">
            <button class="btn btn-primary btn-sm" onclick="mysky.approveAccessRequest('${esc(r.id)}')">Approve</button>
            <button class="btn btn-ghost btn-sm" onclick="mysky.denyAccessRequest('${esc(r.id)}')">Deny</button>
            <button class="btn btn-ghost btn-sm" onclick="mysky.openDetail('${esc(r.lead_id)}')">View</button>
          </div>
        </div>`).join('');
  } catch {
    box.classList.add('hidden');
    box.innerHTML = '';
  }
}

async function approveAccessRequest(id) {
  try {
    await api('/api/lead-access/requests/' + id + '/approve', { method: 'POST' });
    showToast('Edit access granted', 'success');
    loadAccessInbox();
    if (state.detailId) await openDetail(state.detailId);
  } catch (e) {
    showToast(e.message || 'Could not approve', 'error');
  }
}

async function denyAccessRequest(id) {
  try {
    await api('/api/lead-access/requests/' + id + '/deny', { method: 'POST' });
    showToast('Request denied', 'warning');
    loadAccessInbox();
  } catch (e) {
    showToast(e.message || 'Could not deny', 'error');
  }
}

function populateStatusSelects() {
  const filterType = $('fType')?.value || state.filter.type || '';
  const types = filterType ? [filterType] : ['visa', 'tour', 'ticket'];
  const statusOpts = [];
  types.forEach((t) => {
    getStatusesForType(t).forEach((s) => {
      if (!statusOpts.some((o) => o.value === s)) {
        statusOpts.push({ value: s, label: STATUS_CFG[s]?.label || STATUS_LABELS[s] || s });
      }
    });
  });
  statusOpts.sort((a, b) => a.label.localeCompare(b.label));
  const optsHtml = statusOpts.map((o) => `<option value="${o.value}">${esc(o.label)}</option>`).join('');

  const fStatus = $('fStatus');
  if (fStatus) {
    const cur = fStatus.value;
    fStatus.innerHTML = '<option value="">All stages</option>' + optsHtml;
    if (cur && statusOpts.some((o) => o.value === cur)) fStatus.value = cur;
  }
}

function populateDetailStatusSelect(lead) {
  const el = $('dStatus');
  if (!el || !lead) return;
  const cur = el.value || lead.status;
  const opts = getStatusesForType(lead.type);
  el.innerHTML = opts.map((s) =>
    `<option value="${s}">${esc(STATUS_CFG[s]?.label || STATUS_LABELS[s] || s)}</option>`
  ).join('');
  if (opts.includes(cur)) el.value = cur;
  else el.value = opts[0];
}

function updateDetailStatusHighlight(status) {
  const sc = STATUS_CFG[status] || STATUS_CFG.new;
  if ($('dStatus')) $('dStatus').value = status;
  const badge = $('dStatusBadge');
  if (badge) {
    badge.className = `badge ${sc.cls}`;
    if ($('dStatusLabel')) $('dStatusLabel').textContent = sc.label;
  }
}

function getStatusStageTabs(type, leadStatus, pending, lead) {
  const pipe = getPipeline(type).filter((s) => !isClosedStatus(s));
  const active = pending || leadStatus;
  const activeIdx = pipe.indexOf(active);
  if (activeIdx < 0) {
    return [{
      status: active,
      step: 1,
      label: stageTabShortLabel(active),
      fullLabel: STATUS_CFG[active]?.label || STATUS_LABELS[active] || active,
      state: 'current',
      hasData: stageHasSavedData(lead, active) || active === leadStatus,
    }];
  }
  return pipe.map((status, i) => ({
    status,
    step: i + 1,
    label: stageTabShortLabel(status),
    fullLabel: STATUS_CFG[status]?.label || STATUS_LABELS[status] || status,
    state: i < activeIdx ? 'past' : i === activeIdx ? 'current' : 'future',
    hasData: stageHasSavedData(lead, status) || status === leadStatus,
  }));
}

function selectDetailStageTab(status) {
  if (!status || state.detailStageTab === status) return;
  state.detailStageTab = status;
  renderDetailStagePanel({ scroll: false });
}

function renderStatusStageTabs(type, lead, pending) {
  const tabsEl = $('dStageTabs');
  if (!tabsEl || !lead) return;
  const activeStatus = pending || lead.status;
  const viewStatus = state.detailStageTab || activeStatus;
  const tabs = getStatusStageTabs(type, lead.status, pending, lead);
  if (tabs.length < 2) {
    tabsEl.classList.add('hidden');
    tabsEl.innerHTML = '';
    return;
  }
  tabsEl.classList.remove('hidden');
  tabsEl.innerHTML = tabs.map((tab) => {
    const selected = tab.status === viewStatus;
    const isDone = tab.state === 'past';
    const cls = [
      'status-stage-tab',
      tab.state,
      selected ? 'selected' : '',
      tab.hasData ? 'has-data' : '',
      tab.status === activeStatus ? 'is-active-stage' : '',
      isDone ? 'is-done' : '',
    ].filter(Boolean).join(' ');
    const check = isDone && tab.hasData
      ? '<svg class="icon tab-check" aria-hidden="true"><use href="/icons.svg#check"/></svg>'
      : '';
    return `<button type="button" class="${cls}" data-stage-tab="${tab.status}" title="${esc(tab.fullLabel)}" aria-current="${selected ? 'step' : 'false'}">
      ${check}
      <span class="tab-label">${esc(tab.label)}</span>
    </button>`;
  }).join('');
  syncScrollStrip(tabsEl, '.status-stage-tab.selected');
}

function renderStatusPipelineTrack(type, status, pending) {
  renderStatusStageTabs(type, state.detailLead, pending);
}

function updateNextStatusButton() {
  const btn = $('dStageNextBtn');
  const saveBtn = $('dStageSaveBtn');
  const lead = state.detailLead;
  if (!btn || !lead) return;
  const poolView = isDetailPoolIntakeView(lead);
  const canEdit = detailCanEdit(lead) && !poolView;
  const baseStatus = state.pendingStatus || lead.status;
  const next = getNextStatus(lead.type, baseStatus);
  const type = lead.type || 'tour';
  const blockProceed = stageProceedBlocked(type, baseStatus, lead);
  btn.classList.toggle('hidden', !canEdit || !next || poolView);
  saveBtn?.classList.toggle('hidden', poolView || (type === 'ticket' && baseStatus === 'new' && isTicketQuickIntake(lead)));
  if (next) {
    const label = STATUS_CFG[next]?.label || STATUS_LABELS[next] || next;
    btn.textContent = `Proceed to ${label} →`;
    btn.disabled = !!blockProceed;
    btn.title = blockProceed ? 'Complete required fields first' : '';
  } else {
    btn.disabled = false;
    btn.title = '';
  }
  syncScrollStrip(btn.closest('.detail-action-bar'), null);
}

function toggleReminderPopover(force) {
  const pop = $('dReminderPopover');
  const btn = $('dReminderBtn');
  if (!pop || !btn) return;
  const open = typeof force === 'boolean' ? force : pop.classList.contains('hidden');
  closeDetailPopovers();
  if (open) {
    pop.classList.remove('hidden');
    btn.setAttribute('aria-expanded', 'true');
    $('rmDateTime')?.focus();
  }
}

function toggleOverflowMenu(force) {
  const menu = $('dOverflowMenu');
  const btn = $('dOverflowBtn');
  if (!menu || !btn) return;
  const open = typeof force === 'boolean' ? force : menu.classList.contains('hidden');
  closeDetailPopovers();
  if (open) {
    menu.classList.remove('hidden');
    btn.setAttribute('aria-expanded', 'true');
  }
}

function initDetailPopovers() {
  const rBtn = $('dReminderBtn');
  if (rBtn && !rBtn.dataset.bound) {
    rBtn.dataset.bound = '1';
    rBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleReminderPopover();
    });
  }

  const oBtn = $('dOverflowBtn');
  if (oBtn && !oBtn.dataset.bound) {
    oBtn.dataset.bound = '1';
    oBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleOverflowMenu();
    });
  }

  $('dManualStatusToggle')?.addEventListener('click', (e) => {
    e.stopPropagation();
    $('dManualStatusPanel')?.classList.toggle('hidden');
  });

  if (!document.detailPopoverBound) {
    document.detailPopoverBound = true;
    document.addEventListener('click', (e) => {
      if (e.target.closest('#dReminderBtn, #dReminderPopover, #dOverflowBtn, #dOverflowMenu')) return;
      closeDetailPopovers();
    });
  }
}

function setStatusOptions(selectEl, { newOnly, type }) {
  if (!selectEl) return;
  const leadType = type || $('m_Type')?.value || 'tour';
  const opts = newOnly ? ['new'] : getStatusesForType(leadType);
  const cur = selectEl.value;
  selectEl.innerHTML = opts.map((s) =>
    `<option value="${s}">${esc(STATUS_CFG[s]?.label || STATUS_LABELS[s] || s)}</option>`
  ).join('');
  if (opts.includes(cur)) selectEl.value = cur;
  else selectEl.value = opts[0];
  selectEl.disabled = newOnly;
}

function isIndiaEvisaPackage(name) {
  const s = String(name || '');
  return /india\s*(e\s*)?visa/i.test(s) || (/india/i.test(s) && /visa/i.test(s));
}

function formatIndiaEvisa(extra) {
  const ie = extra?.india_evisa;
  if (!ie) return '';
  const purpose = ie.purpose === 'business' ? 'Business eVisa' : ie.purpose === 'other' ? 'Other eVisa' : 'Tourist eVisa';
  const duration = ie.duration === '1y' ? '1 Year' : '30 Days';
  return `${purpose} · ${duration}`;
}

function formatPax(extra) {
  if (extra?.per_adt_itineraries && extra?.adt_profiles?.length) {
    const base = formatTicketPax(extra);
    const routes = formatAdtItinerarySummary(extra);
    return routes ? `${base} · ${routes}` : base;
  }
  return formatTicketPax(extra);
}

const PHONE_CODES = ['+971', '+966', '+94', '+91', '+65', '+60', '+66', '+84', '+44', '+61', '+1'];
const PHONE_JUNK_RE = /[\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF\u00A0\u202F]/g;

function stripPhoneJunk(raw) {
  return String(raw || '').replace(PHONE_JUNK_RE, '').trim();
}

function formatPhoneDisplay(raw) {
  const compact = stripPhoneJunk(raw).replace(/\s+/g, '');
  if (!compact) return '';
  const digits = compact.replace(/[^0-9]/g, '');
  if (!digits) return '';

  if (compact.includes('+') || (digits.startsWith('94') && digits.length > 9)) {
    for (const c of [...PHONE_CODES].sort((a, b) => b.length - a.length)) {
      const cc = c.slice(1);
      if (digits.startsWith(cc) && digits.length > cc.length) {
        return `${c}${digits.slice(cc.length)}`;
      }
    }
    return `+${digits}`;
  }
  if (/^0/.test(compact)) return `+94${digits.replace(/^0+/, '')}`;
  if (digits.length === 9 && digits.startsWith('7')) return `+94${digits}`;
  return `+94${digits}`;
}

function normalizePhoneInput(raw) {
  return formatPhoneDisplay(raw);
}

function getFullPhone() {
  return normalizePhoneInput($('m_Phone')?.value);
}

function setPhoneFields(phone, normalized) {
  if (normalized) {
    const digits = String(normalized).replace(/[^0-9]/g, '');
    for (const c of [...PHONE_CODES].sort((a, b) => b.length - a.length)) {
      const cc = c.slice(1);
      if (digits.startsWith(cc) && digits.length > cc.length) {
        if ($('m_Phone')) $('m_Phone').value = `${c}${digits.slice(cc.length)}`;
        return;
      }
    }
    if ($('m_Phone')) $('m_Phone').value = digits ? `+${digits}` : '';
    return;
  }
  if (!phone) {
    if ($('m_Phone')) $('m_Phone').value = '';
    return;
  }
  if ($('m_Phone')) $('m_Phone').value = formatPhoneDisplay(phone);
}

function applyPhoneFieldFormat() {
  const el = $('m_Phone');
  if (!el) return;
  const formatted = formatPhoneDisplay(el.value);
  if (formatted !== el.value) el.value = formatted;
}

function updatePaxPreview() {
  const adt = Number($('m_PaxAdt')?.value) || 0;
  const stu = Number($('m_PaxStu')?.value) || 0;
  const chd = Number($('m_PaxChd')?.value) || 0;
  const inf = Number($('m_PaxInf')?.value) || 0;
  const parts = [];
  if (adt) parts.push(`${adt}ADT`);
  if (stu && $('m_Type')?.value === 'ticket') parts.push(`${stu}STU`);
  if (chd) parts.push(`${chd}CHD`);
  if (inf) parts.push(`${inf}INF`);
  if ($('m_PaxPreview')) $('m_PaxPreview').textContent = parts.length ? parts.join(' ') : '—';
  const total = adt + stu + chd + inf;
  if ($('m_Passengers')) $('m_Passengers').value = total || '';
  if ($('m_Type')?.value === 'ticket') syncPerAdtUi();
  updatePaxRateTotals();
}

function calcPaxRateTotal(type) {
  if (type === 'ticket') return calcTicketCostTotal();
  const adt = Number($('m_PaxAdt')?.value) || 0;
  const chd = Number($('m_PaxChd')?.value) || 0;
  const inf = Number($('m_PaxInf')?.value) || 0;
  const rateAdt = Number($('m_RateAdt')?.value) || 0;
  const rateChd = Number($('m_RateChd')?.value) || 0;
  const rateInf = Number($('m_RateInf')?.value) || 0;
  const chdRate = rateChd || (type === 'tour' && chd > 0 ? rateAdt : 0);
  if (!rateAdt && !rateChd && !rateInf && !chdRate) return null;
  return (adt * rateAdt) + (chd * chdRate) + (inf * rateInf);
}

function updatePaxRateTotals() {
  const type = $('m_Type')?.value;
  const costEl = $('m_QuoteCost');
  const profitEl = $('m_QuoteProfit');
  const totalEl = $('m_QuoteTotal');
  const fmtTotal = (n) => (n != null && n > 0 ? fmtAmt(n) : '—');

  if (type === 'ticket') {
    const breakdown = calcTicketPricingBreakdown();
    if (costEl) costEl.textContent = fmtTotal(breakdown?.cost);
    if (profitEl) profitEl.textContent = fmtTotal(breakdown?.profit);
    if (totalEl) totalEl.textContent = fmtTotal(breakdown?.total);
  } else {
    const total = calcPaxRateTotal(type);
    if (type === 'visa') {
      const costPerAdt = Number($('m_VisaCost')?.value) || 0;
      const costTotal = costPerAdt > 0
        ? visaCostTotal({ pax: { adt: Number($('m_PaxAdt')?.value) || 0, chd: Number($('m_PaxChd')?.value) || 0 } }, costPerAdt)
        : null;
      if (costEl) costEl.textContent = fmtTotal(costTotal);
      if (profitEl) {
        profitEl.textContent = (costTotal != null && total != null && total > 0)
          ? fmtTotal(total - costTotal)
          : '—';
      }
    } else {
      if (costEl) costEl.textContent = '—';
      if (profitEl) profitEl.textContent = '—';
    }
    if (totalEl) totalEl.textContent = fmtTotal(total);
    if (total != null && total > 0 && $('m_Amount')) {
      $('m_Amount').value = total;
    }
  }
}

function updatePaxQuoteTotal() {
  updatePaxRateTotals();
}

function setPaxRateFields(extra, { cost = false } = {}) {
  const r = (cost ? extra?.pax_cost_rates : extra?.pax_rates) || {};
  if ($('m_RateAdt')) $('m_RateAdt').value = r.adt ?? '';
  if ($('m_RateStu')) $('m_RateStu').value = r.stu ?? '';
  if ($('m_RateChd')) $('m_RateChd').value = r.chd ?? '';
  if ($('m_RateInf')) $('m_RateInf').value = r.inf ?? '';
  const pr = extra?.pax_profit_rates || {};
  if ($('m_ProfitAdt')) $('m_ProfitAdt').value = pr.adt ?? '';
  if ($('m_ProfitStu')) $('m_ProfitStu').value = pr.stu ?? '';
  if ($('m_ProfitChd')) $('m_ProfitChd').value = pr.chd ?? '';
  if ($('m_ProfitInf')) $('m_ProfitInf').value = pr.inf ?? '';
  updatePaxRateTotals();
}

function setPaxFields(extra) {
  const p = extra?.pax || {};
  if ($('m_PaxAdt')) $('m_PaxAdt').value = p.adt ?? '';
  if ($('m_PaxStu')) $('m_PaxStu').value = p.stu ?? '';
  if ($('m_PaxChd')) $('m_PaxChd').value = p.chd ?? '';
  if ($('m_PaxInf')) $('m_PaxInf').value = p.inf ?? '';
  updatePaxPreview();
}

function updateTourDurationPreview() {
  const n = $('m_TourN')?.value;
  const d = $('m_TourD')?.value;
  let txt = 'e.g. 4N 5D';
  if (n && d) txt = `${n}N ${d}D`;
  else if (n) txt = `${n}N`;
  else if (d) txt = `${d}D`;
  if ($('m_TourDurPreview')) $('m_TourDurPreview').textContent = txt;
}

function setTourDurationFields(extra) {
  const td = extra?.tour_duration || '';
  const m = td.match(/(\d+)N\s*(\d+)D/i);
  const nOnly = td.match(/^(\d+)N$/i);
  const dOnly = td.match(/^(\d+)D$/i);
  if ($('m_TourN')) $('m_TourN').value = m ? m[1] : (nOnly ? nOnly[1] : '');
  if ($('m_TourD')) $('m_TourD').value = m ? m[2] : (dOnly ? dOnly[1] : '');
  updateTourDurationPreview();
}

function selectMiniSeg(btn) {
  const seg = btn.closest('.mini-seg');
  if (!seg) return;
  seg.querySelectorAll('.mini-seg-btn').forEach((b) => {
    b.classList.remove('active');
    b.setAttribute('aria-pressed', 'false');
  });
  btn.classList.add('active');
  btn.setAttribute('aria-pressed', 'true');
}

function getIndiaEvisaSelections() {
  const purpose = document.querySelector('#indiaPurposeSeg .mini-seg-btn.active')?.dataset.value || '';
  const duration = document.querySelector('#indiaDurationSeg .mini-seg-btn.active')?.dataset.value || '';
  return { purpose, duration };
}

function setIndiaEvisaSelections(extra) {
  const ie = extra?.india_evisa || {};
  document.querySelectorAll('#indiaPurposeSeg .mini-seg-btn').forEach((b) => {
    const on = b.dataset.value === (ie.purpose || 'tourist');
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  document.querySelectorAll('#indiaDurationSeg .mini-seg-btn').forEach((b) => {
    const on = b.dataset.value === (ie.duration || '30d');
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

function onPackageChange() {
  const pkg = $('m_Package')?.value || '';
  const show = isIndiaEvisaPackage(pkg);
  $('indiaEvisaWrap')?.classList.toggle('hidden', !show);
  if (show && !document.querySelector('#indiaPurposeSeg .mini-seg-btn.active')) {
    setIndiaEvisaSelections({});
  }
  if ($('m_Type')?.value === 'visa' && pkg) {
    const costs = state.settings?.visa_costs || {};
    const el = $('m_VisaCost');
    if (el && !el.dataset.userEdited && costs[pkg] != null) {
      el.value = costs[pkg];
    }
  }
}

function buildExtraFromForm() {
  const extra = {};
  const type = $('m_Type')?.value;
  const adt = Number($('m_PaxAdt')?.value) || 0;
  const stu = Number($('m_PaxStu')?.value) || 0;
  const chd = Number($('m_PaxChd')?.value) || 0;
  const inf = Number($('m_PaxInf')?.value) || 0;
  if (adt || stu || chd || inf) {
    extra.pax = {
      adt,
      stu: type === 'ticket' ? stu : 0,
      chd,
      inf,
    };
  } else if (state.editingId) extra.pax = null;

  const rateAdt = Number($('m_RateAdt')?.value) || 0;
  const rateStu = Number($('m_RateStu')?.value) || 0;
  const rateChd = Number($('m_RateChd')?.value) || 0;
  const rateInf = Number($('m_RateInf')?.value) || 0;
  const profitAdt = Number($('m_ProfitAdt')?.value) || 0;
  const profitStu = Number($('m_ProfitStu')?.value) || 0;
  const profitChd = Number($('m_ProfitChd')?.value) || 0;
  const profitInf = Number($('m_ProfitInf')?.value) || 0;
  if (type === 'ticket') {
    const mode = $('m_DateFlexMode')?.value || 'fixed';
    extra.date_flex = {
      mode,
      search_from: mode === 'flexible' ? ($('m_SearchFrom')?.value || null) : null,
      search_to: mode === 'flexible' ? ($('m_SearchTo')?.value || null) : null,
    };
    extra.preferences = {
      baggage: $('m_BaggagePref')?.value || null,
      seat: $('m_SeatPref')?.value || null,
      transit: $('m_TransitPref')?.value || 'direct',
      transit_via: $('m_TransitVia')?.value?.trim().toUpperCase() || null,
    };
    const splitRates = isPerAdtMode() && shouldOfferPerAdtMode();
    if (splitRates) {
      if (rateChd || rateInf) {
        extra.pax_cost_rates = { chd: rateChd, inf: rateInf };
      } else if (state.editingId) {
        extra.pax_cost_rates = null;
      }
      if (profitChd || profitInf) {
        extra.pax_profit_rates = { chd: profitChd, inf: profitInf };
      } else if (state.editingId) {
        extra.pax_profit_rates = null;
      }
    } else if (rateAdt || rateStu || rateChd || rateInf) {
      extra.pax_cost_rates = { adt: rateAdt, stu: rateStu, chd: rateChd, inf: rateInf };
    } else if (state.editingId) {
      extra.pax_cost_rates = null;
    }
    if (!splitRates) {
      if (profitAdt || profitStu || profitChd || profitInf) {
        extra.pax_profit_rates = { adt: profitAdt, stu: profitStu, chd: profitChd, inf: profitInf };
      } else if (state.editingId) {
        extra.pax_profit_rates = null;
      }
    }
    extra.pax_rates = null;
    buildAdtExtraFields(extra, type);
  } else {
    if (rateAdt || rateChd || rateInf) {
      const chdSell = rateChd || (type === 'tour' && chd > 0 ? rateAdt : null);
      extra.pax_rates = { adt: rateAdt || null, chd: chdSell, inf: rateInf || null };
    } else if (state.editingId) {
      extra.pax_rates = null;
    }
    if (type === 'visa') {
      const costPerAdt = Number($('m_VisaCost')?.value) || 0;
      if (costPerAdt > 0) {
        extra.pax_cost_rates = {
          adt: costPerAdt,
          ...(chd > 0 ? { chd: costPerAdt } : {}),
        };
        const sellAdt = rateAdt || 0;
        const sellChd = rateChd || sellAdt;
        const profits = {};
        if (sellAdt > 0) profits.adt = sellAdt - costPerAdt;
        if (chd > 0 && sellChd > 0) profits.chd = sellChd - costPerAdt;
        if (Object.keys(profits).length) extra.pax_profit_rates = profits;
        else if (state.editingId) extra.pax_profit_rates = null;
      } else if (state.editingId) {
        extra.pax_cost_rates = null;
        extra.pax_profit_rates = null;
      }
    } else {
      extra.pax_cost_rates = null;
      extra.pax_profit_rates = null;
    }
  }

  const email = $('m_Email')?.value.trim();
  if (email) extra.email = email;
  else if (state.editingId) extra.email = null;

  const passport = $('m_Passport')?.value.trim();
  if (passport) extra.passport_no = passport;
  else if (state.editingId) extra.passport_no = null;

  if (type === 'tour') {
    const n = $('m_TourN')?.value;
    const d = $('m_TourD')?.value;
    if (n && d) extra.tour_duration = `${n}N ${d}D`;
    else if (n) extra.tour_duration = `${n}N`;
    else if (d) extra.tour_duration = `${d}D`;
    else if (state.editingId) extra.tour_duration = null;
  } else if (state.editingId) {
    extra.tour_duration = null;
  }

  if (isIndiaEvisaPackage($('m_Package')?.value)) {
    const { purpose, duration } = getIndiaEvisaSelections();
    if (purpose && duration) extra.india_evisa = { purpose, duration };
  }

  if (type === 'ticket') {
    if (!isPerAdtMode() && $('m_TripType')?.value === 'multi') {
      const segments = collectMultiCitySegments().filter((s) => s.origin || s.destination || s.depart_date);
      if (segments.length) extra.flight_segments = segments;
      else if (state.editingId) extra.flight_segments = null;
    } else if (state.editingId) {
      extra.flight_segments = null;
    }
  } else if (state.editingId) {
    extra.flight_segments = null;
  }

  return extra;
}

function setInquiryMode(isNew) {
  $('inquiryModal')?.classList.toggle('is-new-mode', isNew);
  $('inquiryModal')?.classList.toggle('is-edit-mode', !isNew);
  document.querySelectorAll('.edit-only').forEach((el) => el.classList.toggle('hidden', isNew));
  $('saveAnotherBtn')?.classList.toggle('hidden', !isNew);
  $('inquiryFooterBar')?.classList.toggle('has-another', isNew);
  $('modalSub').textContent = isNew
    ? 'Client & trip first — expand sections as needed'
    : 'All sections open — update and save';
  const more = $('moreFields');
  const toggle = $('moreToggle');
  if (isNew) {
    more?.classList.add('hidden');
    toggle?.classList.remove('open');
    if ($('moreLabel')) $('moreLabel').textContent = 'More options';
    resetInquiryCardCollapse({ openClient: true, openTrip: true });
  } else {
    more?.classList.remove('hidden');
    toggle?.classList.add('open');
    if ($('moreLabel')) $('moreLabel').textContent = 'Hide options';
    resetInquiryCardCollapse({ openAll: true });
  }
}

function toggleMoreFields() {
  const more = $('moreFields');
  const toggle = $('moreToggle');
  const open = more.classList.toggle('hidden') === false;
  toggle.classList.toggle('open', open);
  $('moreLabel').textContent = open ? 'Hide options' : 'More options';
}

function selectType(type) {
  $('m_Type').value = type;
  document.querySelectorAll('.type-seg-btn').forEach((btn) => {
    const active = btn.dataset.type === type;
    btn.classList.toggle('active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
  const chip = $('inquiryTypeChip');
  if (chip) {
    chip.textContent = type;
    chip.className = `type-chip ${type}`;
  }
  onTypeChange();
}

function selectDateFlexMode(mode) {
  if ($('m_DateFlexMode')) $('m_DateFlexMode').value = mode;
  document.querySelectorAll('#dateFlexSeg .mini-seg-btn').forEach((b) => {
    const on = b.dataset.value === mode;
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  $('flexDateWrap')?.classList.toggle('hidden', mode !== 'flexible');
}

function selectTransitPref(pref) {
  if ($('m_TransitPref')) $('m_TransitPref').value = pref;
  document.querySelectorAll('#transitPrefSeg .mini-seg-btn').forEach((b) => {
    const on = b.dataset.value === pref;
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  $('transitViaWrap')?.classList.toggle('hidden', pref !== 'preferred');
}

function setTicketRequirementFields(extra = {}) {
  const df = extra.date_flex || {};
  selectDateFlexMode(df.mode === 'flexible' ? 'flexible' : 'fixed');
  if ($('m_SearchFrom')) $('m_SearchFrom').value = df.search_from || '';
  if ($('m_SearchTo')) $('m_SearchTo').value = df.search_to || '';
  const prefs = extra.preferences || {};
  if ($('m_BaggagePref')) $('m_BaggagePref').value = prefs.baggage || '';
  if ($('m_SeatPref')) $('m_SeatPref').value = prefs.seat || '';
  selectTransitPref(prefs.transit || 'direct');
  if ($('m_TransitVia')) $('m_TransitVia').value = prefs.transit_via || '';
}

function formatTicketPreferences(extra = {}) {
  const prefs = extra.preferences || {};
  const parts = [];
  const bag = { cabin: 'Cabin only', '23kg': '23kg checked', '30kg': '30kg+ checked', extra: 'Extra baggage' };
  if (prefs.baggage && bag[prefs.baggage]) parts.push(bag[prefs.baggage]);
  const seat = { window: 'Window', aisle: 'Aisle', middle: 'Middle', exit: 'Exit row', front: 'Front' };
  if (prefs.seat && seat[prefs.seat]) parts.push(`${seat[prefs.seat]} seat`);
  if (prefs.transit === 'direct') parts.push('Direct');
  else if (prefs.transit === 'multi' && prefs.multi_city_route) parts.push(`Multi-city ${prefs.multi_city_route}`);
  else if (prefs.transit === 'transit' && prefs.transit_via) parts.push(`Transit via ${prefs.transit_via}`);
  else if (prefs.transit === 'transit' || prefs.transit === 'ok') parts.push('Transit OK');
  else if (prefs.transit === 'preferred' && prefs.transit_via) parts.push(`Via ${prefs.transit_via}`);
  return parts.length ? parts.join(' · ') : '';
}

function defaultMultiCitySegment() {
  return { origin: '', destination: '', depart_date: '', depart_time: '', arrive_time: '' };
}

function formatTicketRouteSummary(l) {
  if (l.trip_type === 'multi' && l.extra?.flight_segments?.length) {
    let route = l.extra.flight_segments[0]?.origin || '—';
    l.extra.flight_segments.forEach((s) => { route += `→${s.destination || '?'}`; });
    return `${route} · multi`;
  }
  return `${l.origin || '—'} → ${l.destination || '—'}`;
}

function formatTicketTravelLine(l) {
  if (l.extra?.per_adt_itineraries && l.extra?.adt_profiles?.length) {
    const lines = l.extra.adt_profiles.map((pr, i) => {
      const route = pr.trip_type === 'multi' && pr.flight_segments?.length
        ? pr.flight_segments.map((s) => `${s.origin || '?'}→${s.destination || '?'}`).join(' · ')
        : `${pr.origin || '?'}→${pr.destination || '?'}`;
      const dates = pr.trip_type === 'return' && pr.return_date
        ? `${fmtDate(pr.depart_date)} → ${fmtDate(pr.return_date)}`
        : fmtDate(pr.depart_date);
      const label = travelerDisplayName('adt', i, pr);
      return `${label}: ${route} ${dates}`.trim();
    });
    if (l.extra?.stu_profiles?.length) {
      lines.push(...l.extra.stu_profiles.map((pr, i) => {
        const route = pr.trip_type === 'multi' && pr.flight_segments?.length
          ? pr.flight_segments.map((s) => `${s.origin || '?'}→${s.destination || '?'}`).join(' · ')
          : `${pr.origin || '?'}→${pr.destination || '?'}`;
        const dates = pr.trip_type === 'return' && pr.return_date
          ? `${fmtDate(pr.depart_date)} → ${fmtDate(pr.return_date)}`
          : fmtDate(pr.depart_date);
        const label = travelerDisplayName('stu', i, pr);
        return `${label}: ${route} ${dates}`.trim();
      }));
    }
    return lines.join(' | ');
  }
  if (l.trip_type === 'multi' && l.extra?.flight_segments?.length) {
    return l.extra.flight_segments.map((s) => {
      const parts = [`${s.origin || '?'}→${s.destination || '?'}`];
      if (s.depart_date) parts.push(fmtDate(s.depart_date));
      const times = [s.depart_time, s.arrive_time].filter(Boolean).join('–');
      if (times) parts.push(times);
      return parts.join(' ');
    }).join(' · ');
  }
  const df = l.extra?.date_flex;
  if (df?.mode === 'flexible' && df.search_from) {
    const range = df.search_to && df.search_to !== df.search_from
      ? `${fmtDate(df.search_from)} – ${fmtDate(df.search_to)}`
      : fmtDate(df.search_from);
    return `Flexible: ${range}`;
  }
  return `${fmtDate(l.depart_date)}${l.return_date ? ' → ' + fmtDate(l.return_date) : ''}`;
}

function multiCitySegmentHtml(i, s, total) {
  const canRemove = total > 2;
  return `<div class="multi-city-seg" data-seg-idx="${i}">
    <div class="multi-city-seg-head">
      <span class="multi-city-seg-label">Flight ${i + 1}</span>
      ${canRemove
    ? `<button type="button" class="btn btn-ghost btn-sm seg-remove" data-seg-remove="${i}" aria-label="Remove flight ${i + 1}">Remove</button>`
    : ''}
    </div>
    <div class="multi-city-seg-fields">
      <div class="multi-city-seg-route">
        <div class="form-group">
          <label class="form-label">From</label>
          <input class="form-input seg-origin route-code" value="${esc(s.origin || '')}" placeholder="CMB" maxlength="8" autocomplete="off">
        </div>
        <span class="route-arrow" aria-hidden="true">→</span>
        <div class="form-group">
          <label class="form-label">To</label>
          <input class="form-input seg-dest route-code" value="${esc(s.destination || '')}" placeholder="DXB" maxlength="8" autocomplete="off">
        </div>
        <div class="form-group seg-date-wrap">
          <label class="form-label">Date</label>
          <input type="date" class="form-input date-input seg-date" value="${esc(s.depart_date || '')}">
        </div>
      </div>
      <div class="multi-city-seg-meta">
        <div class="form-group">
          <label class="form-label">Depart</label>
          <input type="time" class="form-input seg-dep-time" value="${esc(s.depart_time || '')}">
        </div>
        <div class="form-group">
          <label class="form-label">Arrive</label>
          <input type="time" class="form-input seg-arr-time" value="${esc(s.arrive_time || '')}">
        </div>
      </div>
    </div>
  </div>`;
}

function renderMultiCitySegments(segments) {
  const box = $('multiCitySegments');
  if (!box) return;
  const segs = segments?.length >= 2 ? segments : [defaultMultiCitySegment(), defaultMultiCitySegment()];
  box.innerHTML = segs.map((s, i) => multiCitySegmentHtml(i, s, segs.length)).join('');
}

function collectMultiCitySegments() {
  const rows = [...document.querySelectorAll('#multiCitySegments .multi-city-seg')];
  return rows.map((row) => ({
    origin: row.querySelector('.seg-origin')?.value.trim().toUpperCase() || '',
    destination: row.querySelector('.seg-dest')?.value.trim().toUpperCase() || '',
    depart_date: row.querySelector('.seg-date')?.value || '',
    depart_time: row.querySelector('.seg-dep-time')?.value || '',
    arrive_time: row.querySelector('.seg-arr-time')?.value || '',
  }));
}

function seedMultiCityFromSimple() {
  const origin = $('m_Origin')?.value.trim().toUpperCase() || '';
  const dest = $('m_Destination')?.value.trim().toUpperCase() || '';
  const date = $('m_Depart')?.value || '';
  if (origin || dest || date) {
    return [{ origin, destination: dest, depart_date: date, depart_time: '', arrive_time: '' }, defaultMultiCitySegment()];
  }
  return [defaultMultiCitySegment(), defaultMultiCitySegment()];
}

function syncSimpleFromMulti() {
  const segs = collectMultiCitySegments().filter((s) => s.origin || s.destination || s.depart_date);
  if (!segs.length) return;
  if ($('m_Origin')) $('m_Origin').value = segs[0].origin || '';
  if ($('m_Destination')) $('m_Destination').value = segs[segs.length - 1].destination || '';
  if ($('m_Depart')) $('m_Depart').value = segs[0].depart_date || '';
}

function addMultiCitySegment() {
  const segs = collectMultiCitySegments();
  if (segs.length >= 8) {
    showToast('Maximum 8 flights', 'warning');
    return;
  }
  const last = segs[segs.length - 1];
  segs.push({
    origin: last?.destination || '',
    destination: '',
    depart_date: '',
    depart_time: '',
    arrive_time: '',
  });
  renderMultiCitySegments(segs);
}

function removeMultiCitySegment(idx) {
  const segs = collectMultiCitySegments();
  if (segs.length <= 2) return;
  segs.splice(idx, 1);
  renderMultiCitySegments(segs);
}

function applyMultiCityToBody(body) {
  if (body.type !== 'ticket' || body.trip_type !== 'multi') return;
  const segs = (body.extra?.flight_segments || []).filter((s) => s.origin || s.destination);
  if (!segs.length) return;
  body.origin = segs[0].origin || null;
  body.destination = segs[segs.length - 1].destination || null;
  body.depart_date = segs[0].depart_date || null;
  body.return_date = null;
  body.extra.flight_segments = segs;
}

function syncTripTypeSeg(value) {
  const trip = value || $('m_TripType')?.value || 'one_way';
  document.querySelectorAll('#tripTypeSeg .mini-seg-btn').forEach((b) => {
    const on = b.dataset.value === trip;
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

function selectTripType(value) {
  if ($('m_TripType')) $('m_TripType').value = value;
  syncTripTypeSeg(value);
  onTripTypeChange();
}

function validateTicketSchedule(body) {
  const trip = body.trip_type;
  if (trip === 'multi') {
    const segs = body.extra?.flight_segments || [];
    for (let i = 0; i < segs.length; i += 1) {
      const s = segs[i];
      if (!s.depart_date) return `Enter date for flight ${i + 1}`;
      if (i > 0 && segs[i - 1].depart_date && s.depart_date < segs[i - 1].depart_date) {
        return `Flight ${i + 1} date cannot be before flight ${i}`;
      }
    }
    return null;
  }
  if (!body.depart_date) return 'Enter departure date';
  if (trip === 'return') {
    if (!body.return_date) return 'Enter return date';
    if (body.return_date < body.depart_date) return 'Return date must be on or after departure';
  }
  return null;
}

function onTripTypeChange() {
  const trip = $('m_TripType')?.value;
  syncTripTypeSeg(trip);
  const isMulti = trip === 'multi';
  const showReturn = trip === 'return';
  $('returnWrap')?.classList.toggle('hidden', !showReturn);
  $('simpleRouteWrap')?.classList.toggle('hidden', isMulti);
  $('multiCityWrap')?.classList.toggle('hidden', !isMulti);
  if (!showReturn && $('m_Return')) $('m_Return').value = '';
  if (isMulti) {
    const existing = collectMultiCitySegments().filter((s) => s.origin || s.destination || s.depart_date);
    renderMultiCitySegments(existing.length >= 2 ? existing : seedMultiCityFromSimple());
  } else {
    syncSimpleFromMulti();
  }
}

async function openAddModal(type) {
  if (!$('addModal') || !inquiryModalReady) {
    try { await ensureInquiryModalReady(); } catch (e) { showToast('Could not load inquiry form', 'error'); return; }
  }
  state.editingId = null;
  state.clientDupMatches = [];
  state.clientMatchSelected = null;
  state.clientDupAcknowledged = false;
  hideClientDupBanner();
  const lastType = localStorage.getItem('mysky_last_type') || 'tour';
  const t = type || lastType;
  $('modalTitle').textContent = 'New Inquiry';
  resetForm();
  setInquiryMode(true);
  setStatusOptions($('m_Status'), { newOnly: true });
  selectType(t);
  $('m_InquiryDate').value = todayISO();
  const lastSrc = localStorage.getItem('mysky_last_source') || '';
  if (lastSrc && [...$('m_Source').options].some((o) => o.value === lastSrc)) {
    $('m_Source').value = lastSrc;
  }
  if ($('m_Agent')) $('m_Agent').value = state.agent.id;
  syncAgentFieldUI();
  // Suggest follow-up +1 day
  const d = new Date();
  d.setDate(d.getDate() + 1);
  $('m_Followup').value = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Colombo' });
  showDraftBannerIfAny();
  resetInquiryCardCollapse({ openClient: true, openTrip: true });
  $('addModal').classList.add('open');
  const focusId = t === 'ticket' ? 'm_Phone' : 'm_Name';
  setTimeout(() => $(focusId)?.focus(), 50);
}

async function openEditModal(id) {
  try {
  let data;
  if (state.detailId === id && state.detailLead && state.detailAccess?.can_edit) {
    data = { lead: state.detailLead, access: state.detailAccess };
  } else {
    data = await api('/api/leads/' + id);
  }
  if (!data.access?.can_edit) {
    showToast('Request edit access or take over this inquiry first', 'warning');
    return;
  }
  if (!$('addModal') || !inquiryModalReady) await ensureInquiryModalReady();
  const l = data.lead;
  state.editingId = id;
  $('modalTitle').textContent = 'Edit Inquiry';
  setInquiryMode(false);
  setStatusOptions($('m_Status'), { newOnly: false });
  selectType(l.type);
  $('m_Name').value = l.name;
  setPhoneFields(l.phone, l.phone_normalized);
  $('m_Source').value = l.source || '';
  $('m_Status').value = l.status;
  $('m_Agent').value = l.agent_id || '';
  $('m_InquiryDate').value = l.inquiry_date || '';
  $('m_Followup').value = l.followup_date || '';
  $('m_Amount').value = l.amount ?? '';
  $('m_Package').value = l.package_name || '';
  onPackageChange();
  setIndiaEvisaSelections(l.extra);
  setPaxFields(l.extra);
  setPaxRateFields(l.extra, { cost: l.type === 'ticket' });
  if ($('m_Email')) $('m_Email').value = l.extra?.email || '';
  if ($('m_Passport')) $('m_Passport').value = l.extra?.passport_no || '';
  setTourDurationFields(l.extra);
  if ($('m_VisaCost')) {
    const costPerAdt = l.extra?.pax_cost_rates?.adt;
    if (costPerAdt != null) {
      $('m_VisaCost').value = costPerAdt;
    } else if (l.cost_amount != null && l.extra?.pax) {
      $('m_VisaCost').value = Math.round(Number(l.cost_amount) / visaBillablePax(l.extra));
    } else {
      $('m_VisaCost').value = l.cost_amount ?? '';
    }
    delete $('m_VisaCost').dataset.userEdited;
  }
  if ($('m_TicketCost')) $('m_TicketCost').value = l.cost_amount ?? '';
  $('m_Custom').checked = !!l.is_custom;
  $('m_CustomReq').value = l.custom_req || '';
  if ($('m_Cost')) $('m_Cost').value = l.cost_amount ?? '';
  if ($('m_Sold')) $('m_Sold').value = l.sold_amount ?? '';
  if ($('m_CostTicket')) $('m_CostTicket').value = l.cost_amount ?? '';
  if ($('m_SoldTicket')) $('m_SoldTicket').value = l.sold_amount ?? '';
  $('m_Origin').value = l.origin || '';
  $('m_Destination').value = l.destination || '';
  $('m_Depart').value = l.depart_date || '';
  $('m_Return').value = l.return_date || '';
  $('m_TripType').value = l.trip_type || 'one_way';
  syncTripTypeSeg(l.trip_type || 'one_way');
  if (l.trip_type === 'multi' && l.extra?.flight_segments?.length) {
    renderMultiCitySegments(l.extra.flight_segments);
  } else if ($('multiCitySegments')) {
    $('multiCitySegments').innerHTML = '';
  }
  setAdtProfiles(l.extra || {});
  setTicketRequirementFields(l.extra || {});
  onTripTypeChange();
  $('m_Airline').value = l.airline || '';
  $('m_Pnr').value = l.pnr || '';
  $('m_Passengers').value = l.passengers ?? '';
  $('m_Fare').value = l.fare_amount ?? '';
  $('m_Class').value = l.ticket_class || '';
  if (l.type !== 'ticket' && (l.amount == null || l.amount === '') && l.quote_amount != null && $('m_Amount')) {
    $('m_Amount').value = l.quote_amount;
  }
  updatePaxRateTotals();
  $('m_Note').value = '';
  toggleCustom();
  syncAgentFieldUI();
  if (!canPickAgent()) {
    const pill = $('assignedAgentPill');
    const ag = state.agents.find((a) => a.id === l.agent_id) || state.agent;
    if (pill && ag) {
      pill.innerHTML = `<span class="agent-dot" style="background:${esc(ag.color)}"></span>${esc(ag.name)}`;
    }
  }
  $('addModal').classList.add('open');
  setTimeout(() => $('m_Name')?.focus(), 50);
  } catch (e) {
    showToast(e.message || 'Could not load inquiry', 'error');
  }
}

function resetForm() {
  ['m_Name', 'm_Phone', 'm_Email', 'm_Passport', 'm_TicketIntakeDetails', 'm_Amount', 'm_CustomReq', 'm_Note', 'm_Origin', 'm_Destination',
    'm_Depart', 'm_Return', 'm_Airline', 'm_Pnr', 'm_Passengers', 'm_Fare', 'm_Class', 'm_Followup',
    'm_Cost', 'm_Sold', 'm_CostTicket', 'm_SoldTicket', 'm_PaxAdt', 'm_PaxStu', 'm_PaxChd', 'm_PaxInf',
    'm_RateAdt', 'm_RateStu', 'm_RateChd', 'm_RateInf', 'm_ProfitAdt', 'm_ProfitStu', 'm_ProfitChd', 'm_ProfitInf', 'm_TourN', 'm_TourD', 'm_VisaCost', 'm_TicketCost', 'm_SearchFrom', 'm_SearchTo', 'm_TransitVia']
    .forEach((id) => { const el = $(id); if (el) el.value = ''; });
  if ($('m_QuoteTotal')) $('m_QuoteTotal').textContent = '—';
  if ($('m_QuoteCost')) $('m_QuoteCost').textContent = '—';
  if ($('m_QuoteProfit')) $('m_QuoteProfit').textContent = '—';
  selectDateFlexMode('fixed');
  selectTransitPref('direct');
  if ($('m_BaggagePref')) $('m_BaggagePref').value = '';
  if ($('m_SeatPref')) $('m_SeatPref').value = '';
  if ($('m_VisaCost')) delete $('m_VisaCost').dataset.userEdited;
  if ($('m_Status')) $('m_Status').value = 'new';
  if ($('m_Source')) $('m_Source').value = '';
  if ($('m_Custom')) $('m_Custom').checked = false;
  if ($('m_TripType')) $('m_TripType').value = 'one_way';
  syncTripTypeSeg('one_way');
  if ($('perAdtItinerariesToggle')) $('perAdtItinerariesToggle').checked = false;
  if ($('multiCitySegments')) $('multiCitySegments').innerHTML = '';
  document.querySelectorAll('.mini-seg-btn').forEach((b) => {
    b.classList.remove('active');
    b.setAttribute('aria-pressed', 'false');
  });
  $('indiaEvisaWrap')?.classList.add('hidden');
  updatePaxPreview();
  updateTourDurationPreview();
  onTripTypeChange();
  syncPerAdtUi();
  toggleCustom();
  hideClientDupBanner();
}

function updatePaxRateLabels() {
  const type = $('m_Type')?.value;
  const ratesLabel = $('paxRatesLabel');
  const totalLabel = $('paxRatesTotalLabel');
  const adtLabel = $('paxRateAdtLabel');
  const chdLabel = $('paxRateChdLabel');
  const infLabel = $('paxRateInfLabel');
  if (type === 'ticket') {
    if (ratesLabel) ratesLabel.textContent = 'Est. cost (LKR) per person';
    if (totalLabel) totalLabel.textContent = 'Total price';
    $('quoteCostItem')?.classList.remove('hidden');
    $('quoteProfitItem')?.classList.remove('hidden');
    if (adtLabel) adtLabel.textContent = 'Adult (ADT)';
    if ($('paxRateStuLabel')) $('paxRateStuLabel').textContent = 'Student (STU)';
    if (chdLabel) chdLabel.textContent = 'Child (CHD)';
    if (infLabel) infLabel.textContent = 'Infant (INF)';
    if ($('paxProfitAdtLabel')) $('paxProfitAdtLabel').textContent = 'Adult (ADT)';
    if ($('paxProfitStuLabel')) $('paxProfitStuLabel').textContent = 'Student (STU)';
    if ($('paxProfitChdLabel')) $('paxProfitChdLabel').textContent = 'Child (CHD)';
    if ($('paxProfitInfLabel')) $('paxProfitInfLabel').textContent = 'Infant (INF)';
    $('aggregateStuRateCell')?.classList.remove('hidden');
    $('aggregateStuPaxCell')?.classList.remove('hidden');
    $('aggregateProfitStuCell')?.classList.remove('hidden');
    $('ticketProfitRow')?.classList.remove('hidden');
  } else {
    if (ratesLabel) ratesLabel.textContent = 'Per person quote (LKR)';
    if (totalLabel) totalLabel.textContent = 'Total quote';
    $('quoteCostItem')?.classList.add('hidden');
    $('quoteProfitItem')?.classList.add('hidden');
    if (adtLabel) adtLabel.textContent = 'Adult rate';
    if (chdLabel) chdLabel.textContent = 'Child rate';
    if (infLabel) infLabel.textContent = 'Infant rate';
    $('aggregateStuRateCell')?.classList.add('hidden');
    $('aggregateStuPaxCell')?.classList.add('hidden');
    $('aggregateProfitStuCell')?.classList.add('hidden');
    $('ticketProfitRow')?.classList.add('hidden');
  }
}

function updateTripCardTitle(type) {
  const title = $('tripCardTitle');
  if (!title) return;
  title.textContent = type === 'ticket' ? 'Flight details' : type === 'visa' ? 'Visa details' : 'Tour details';
}

function onTypeChange() {
  const type = $('m_Type').value;
  if (type !== 'ticket') {
    if ($('m_PaxStu')) $('m_PaxStu').value = '';
    if ($('m_RateStu')) $('m_RateStu').value = '';
    if ($('m_ProfitStu')) $('m_ProfitStu').value = '';
  }
  updateTripCardTitle(type);
  document.querySelectorAll('.type-seg-btn').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.type === type);
  });
  $('tourVisaFields').classList.toggle('hidden', type === 'ticket');
  $('ticketFormFields').classList.toggle('hidden', type !== 'ticket');
  $('ticketQuoteHint')?.classList.toggle('hidden', type !== 'ticket');
  $('tourDurationWrap')?.classList.toggle('hidden', type !== 'tour');
  $('visaCostWrap')?.classList.toggle('hidden', type !== 'visa');
  $('ticketCostWrap')?.classList.add('hidden');
  $('passportWrap')?.classList.toggle('hidden', type !== 'ticket');
  $('ticketPricingSection')?.classList.toggle('hidden', type !== 'ticket');
  syncTicketIntakeMode();
  updatePaxRateLabels();
  if ($('pkgLabel')) {
    $('pkgLabel').textContent = type === 'visa' ? 'Visa type *' : 'Package *';
  }
  const pkg = $('m_Package');
  if (pkg) {
    const items = type === 'visa' ? (state.settings.visas || []) : (state.settings.tours || []);
    const prev = pkg.value;
    pkg.innerHTML = '<option value="">Select…</option>' + items.map((i) => `<option value="${esc(i)}">${esc(i)}</option>`).join('');
    if (items.includes(prev)) pkg.value = prev;
  }
  onPackageChange();
  const src = $('m_Source');
  if (src) {
    const cur = src.value;
    src.innerHTML = '<option value="">Select source…</option>' +
      (state.settings.sources || []).map((s) => `<option value="${esc(s)}">${esc(s)}</option>`).join('');
    if ([...src.options].some((o) => o.value === cur)) src.value = cur;
  }
  const agentSel = $('m_Agent');
  if (agentSel) {
    agentSel.innerHTML = state.agents.map((a) => `<option value="${esc(a.id)}">${esc(a.name)}</option>`).join('');
    agentSel.disabled = !canPickAgent();
  }
  // Show tour money vs ticket extras in edit mode
  const isEdit = !!state.editingId;
  $('tourVisaMoney')?.classList.toggle('hidden', type === 'ticket' || !isEdit);
  $('ticketExtraFields')?.classList.toggle('hidden', type !== 'ticket' || !isEdit);
  $('moneyFields')?.classList.toggle('hidden', !isEdit);
  syncAgentFieldUI();
  if (type === 'ticket') syncPerAdtUi();
}

function toggleCustom() {
  $('customWrap')?.classList.toggle('hidden', !$('m_Custom')?.checked);
}

const MATCH_REASON_LABELS = { phone: 'phone', email: 'email', passport: 'passport' };

function formatMatchReasons(reasons = []) {
  return reasons.map((r) => MATCH_REASON_LABELS[r] || r).join(', ') || 'phone';
}

function hideClientDupBanner() {
  $('clientDupBanner')?.classList.add('hidden');
  if ($('clientDupBanner')) $('clientDupBanner').innerHTML = '';
}

async function fetchClientDuplicates() {
  const phone = getFullPhone();
  const email = $('m_Email')?.value.trim() || '';
  const passport = $('m_Passport')?.value.trim() || '';
  if (!phone && !email && !passport) return [];
  const q = new URLSearchParams();
  if (phone) q.set('phone', phone);
  if (email) q.set('email', email);
  if (passport) q.set('passport', passport);
  if (state.editingId) q.set('exclude', state.editingId);
  try {
    const data = await api('/api/check-duplicates?' + q);
    return data.duplicates || [];
  } catch {
    return [];
  }
}

async function checkClientDuplicates() {
  if (state.editingId) {
    hideClientDupBanner();
    return;
  }
  const matches = await fetchClientDuplicates();
  state.clientDupMatches = matches;
  const banner = $('clientDupBanner');
  if (!banner) return;
  if (!matches.length) {
    hideClientDupBanner();
    return;
  }
  banner.classList.remove('hidden');
  banner.innerHTML = `<strong>Existing client match</strong>
    <ul>${matches.slice(0, 5).map((d) =>
      `<li>${esc(d.name)} · ${esc(d.phone || '—')} · matched by ${esc(formatMatchReasons(d.match_reasons))}</li>`
    ).join('')}</ul>
    <span class="field-hint">Saving will ask: same client new inquiry or merge.</span>`;
}

function updateDetailBalanceWarn(l) {
  const el = $('dBalanceWarn');
  if (!el) return;
  const due = leadOutstandingBalance(l);
  const collectDate = l.extra?.balance_collect_date;
  const isTourAdvance = l.type === 'tour' && l.status === 'tour_accepted_advance' && due > 0;
  if (!isTourAdvance) {
    el.classList.add('hidden');
    el.innerHTML = '';
    $('dStagePanel')?.classList.remove('detail-balance-due');
    return;
  }
  el.classList.remove('hidden');
  const canEdit = state.detailAccess?.can_edit !== false && (hasPerm(state.agent, 'edit_all') || l.agent_id === state.agent?.id || l.has_edit_grant);
  el.innerHTML = `<div class="balance-warn-inner">
    <div><strong>Balance payment due</strong> — Collect ${fmtAmt(due)}${collectDate ? ` by ${esc(fmtDate(collectDate))}` : ''}. Follow-up is set to the collect date.</div>
    ${canEdit ? `<div class="btn-bar balance-warn-actions"><button type="button" class="btn btn-primary btn-sm" id="dBalanceCollectBtn">Record payment</button></div>` : ''}
  </div>`;
  $('dStagePanel')?.classList.add('detail-balance-due');
  $('dBalanceCollectBtn')?.addEventListener('click', () => openBalanceCollect(l.id, ''));
}

let bookingExpiryTimer = null;

function updateBookingExpiryCountdown(l) {
  const el = $('dBookingExpiry');
  if (!el) return;
  if (bookingExpiryTimer) {
    clearInterval(bookingExpiryTimer);
    bookingExpiryTimer = null;
  }
  const expires = l.extra?.booking_expires;
  const show = l.type === 'ticket' && l.status === 'temporary_booking' && expires;
  if (!show) {
    el.classList.add('hidden');
    el.innerHTML = '';
    return;
  }
  const tick = () => {
    const ms = new Date(expires).getTime() - Date.now();
    if (!Number.isFinite(ms)) {
      el.classList.add('hidden');
      return;
    }
    el.classList.remove('hidden');
    const urgent = ms <= 3600000;
    el.className = `err-banner${urgent ? ' booking-expiry-urgent' : ''}`;
    if (ms <= 0) {
      el.innerHTML = `<div class="balance-warn-inner"><strong>Booking expired</strong> — PNR ${esc(l.pnr || '—')} · update or cancel.</div>`;
      return;
    }
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    const parts = [];
    if (h) parts.push(`${h}h`);
    parts.push(`${String(m).padStart(2, '0')}m`, `${String(s).padStart(2, '0')}s`);
    el.innerHTML = `<div class="balance-warn-inner"><strong>${urgent ? 'Expires within 1 hour' : 'Booking hold'}</strong> — ${parts.join(' ')} remaining · PNR ${esc(l.pnr || '—')}${l.extra?.booking_system ? ` · ${esc(l.extra.booking_system)}` : ''}</div>`;
  };
  tick();
  bookingExpiryTimer = setInterval(tick, 1000);
}

function renderDupWarn(duplicates, currentId) {
  const el = $('dupWarn');
  if (!el) return;
  if (state.detailLead?.extra?.same_client_new_inquiry) {
    el.classList.add('hidden');
    el.innerHTML = '';
    return;
  }
  el.classList.remove('hidden');
  const canMerge = hasPerm(state.agent, 'edit_all') || state.agent.role === 'admin';
  el.innerHTML = `<strong>Possible duplicate client</strong>
    <ul class="dup-warn-list">${duplicates.map((d) =>
      `<li><button type="button" class="link-btn" data-dup-open="${esc(d.id)}">${esc(d.name)}</button>
        · ${esc(d.phone || '—')} · ${esc(formatMatchReasons(d.match_reasons))}</li>`
    ).join('')}</ul>
    ${canMerge ? '<button type="button" class="btn btn-ghost btn-sm" id="dupMergeBtn">Merge duplicates…</button>' : ''}`;
  el.querySelectorAll('[data-dup-open]').forEach((btn) => {
    btn.addEventListener('click', () => openDetail(btn.dataset.dupOpen));
  });
  $('dupMergeBtn')?.addEventListener('click', () => {
    const leads = [{
      id: currentId,
      name: state.detailLead?.name,
      phone: state.detailLead?.phone,
      type: state.detailLead?.type,
      inquiry_date: state.detailLead?.inquiry_date,
      created_at: state.detailLead?.created_at,
    }, ...duplicates];
    openMergeDupModal(leads);
  });
}

function renderClientMatchList(matches) {
  state.clientMatchSelected = matches[0] || null;
  const list = $('clientMatchList');
  if (!list) return;
  list.innerHTML = matches.map((d, i) => `
    <div class="client-match-item${i === 0 ? ' selected' : ''}" data-match-id="${esc(d.id)}" role="button" tabindex="0">
      <strong>${esc(d.name)}</strong>
      <span>${esc(d.type)} · ${esc(d.status_label || d.status)} · ${esc(d.phone || '—')}</span>
      <span>${d.email ? esc(d.email) + ' · ' : ''}matched by ${esc(formatMatchReasons(d.match_reasons))}</span>
    </div>`).join('');
  list.querySelectorAll('.client-match-item').forEach((item) => {
    const pick = () => {
      list.querySelectorAll('.client-match-item').forEach((x) => x.classList.remove('selected'));
      item.classList.add('selected');
      state.clientMatchSelected = matches.find((m) => m.id === item.dataset.matchId) || null;
    };
    item.addEventListener('click', pick);
    item.addEventListener('keydown', (e) => { if (e.key === 'Enter') pick(); });
  });
}

function closeClientMatchModal(result = null) {
  $('clientMatchModal')?.classList.remove('open');
  if (state.clientMatchResolver) {
    state.clientMatchResolver(result);
    state.clientMatchResolver = null;
  }
}

function promptClientMatch(matches) {
  return new Promise((resolve) => {
    state.clientMatchResolver = resolve;
    renderClientMatchList(matches);
    $('clientAutofillBlock')?.classList.remove('hidden');
    const modal = $('clientMatchModal');
    if (modal) {
      document.body.appendChild(modal);
      modal.classList.add('open');
    }
  });
}

function applyClientAutofill(match, fields = {}) {
  if (!match) return;
  if (fields.name !== false && (fields.name || $('autofillName')?.checked) && match.name) {
    $('m_Name').value = match.name;
  }
  if (fields.phone !== false && (fields.phone || $('autofillPhone')?.checked) && match.phone) {
    setPhoneFields(match.phone, match.phone_normalized);
  }
  if (fields.email !== false && (fields.email || $('autofillEmail')?.checked) && match.email) {
    $('m_Email').value = match.email;
  }
  if (fields.passport !== false && (fields.passport || $('autofillPassport')?.checked) && match.passport_no) {
    $('m_Passport').value = match.passport_no;
  }
}

function getClientMatchAutofill() {
  return {
    name: !!$('autofillName')?.checked,
    phone: !!$('autofillPhone')?.checked,
    email: !!$('autofillEmail')?.checked,
    passport: !!$('autofillPassport')?.checked,
  };
}

function onClientMatchNewEntry() {
  const match = state.clientMatchSelected;
  if (!match) {
    showToast('Select a matching record', 'warning');
    return;
  }
  const autofill = getClientMatchAutofill();
  applyClientAutofill(match, autofill);
  closeClientMatchModal({ action: 'same_client', match, autofill });
}

function onClientMatchMerge() {
  const match = state.clientMatchSelected;
  if (!match) {
    showToast('Select a matching record', 'warning');
    return;
  }
  closeClientMatchModal({ action: 'merge', match });
}

function refreshSaveBodyAfterAutofill(body, phone) {
  body.name = $('m_Name').value.trim();
  body.phone = phone;
  body.extra = buildExtraFromForm();
  applyMultiCityToBody(body);
}

function openMergeDupModal(leads) {
  state.mergeDupLeads = leads || [];
  const primarySel = $('mergePrimarySelect');
  const secWrap = $('mergeSecondaryChecks');
  if (!primarySel || !secWrap || !leads?.length) return;
  primarySel.innerHTML = leads.map((l) =>
    `<option value="${esc(l.id)}">${esc(l.name)} · ${esc(l.type || '')} · ${esc(l.inquiry_date || l.created_at || '')}</option>`
  ).join('');
  const renderSecondaries = () => {
    const primaryId = primarySel.value;
    const secondaries = leads.filter((l) => l.id !== primaryId);
    secWrap.innerHTML = secondaries.length
      ? secondaries.map((l) =>
        `<label class="check-row"><input type="checkbox" class="merge-sec-cb" value="${esc(l.id)}" checked>
          ${esc(l.name)} · ${esc(l.phone || l.phone_display || '—')} · ${esc(l.type || '')}</label>`
      ).join('')
      : '<p class="field-hint">No other records to merge.</p>';
  };
  primarySel.onchange = renderSecondaries;
  renderSecondaries();
  $('mergeDupModal')?.classList.add('open');
}

async function confirmMergeDuplicates() {
  const primaryId = $('mergePrimarySelect')?.value;
  const secondaryIds = [...document.querySelectorAll('.merge-sec-cb:checked')].map((cb) => cb.value);
  if (!primaryId || !secondaryIds.length) {
    showToast('Select at least one inquiry to merge', 'warning');
    return;
  }
  const names = secondaryIds.map((id) => state.mergeDupLeads.find((l) => l.id === id)?.name).filter(Boolean);
  const ok = confirm(`Merge ${names.join(', ')} into the primary inquiry? Notes and call history will be combined.`);
  if (!ok) return;
  try {
    $('mergeDupConfirmBtn').disabled = true;
    await api('/api/leads/merge', { method: 'POST', body: { primary_id: primaryId, secondary_ids: secondaryIds } });
    showToast('Duplicates merged', 'success');
    closeModal('mergeDupModal');
    await refresh();
    if (state.detailId && (secondaryIds.includes(state.detailId) || state.detailId === primaryId)) {
      openDetail(primaryId);
    }
  } catch (ex) {
    showToast(ex.message || 'Merge failed', 'error');
  } finally {
    $('mergeDupConfirmBtn').disabled = false;
  }
}

async function saveLead(andAnother = false) {
  const type = $('m_Type').value;
  const phone = getFullPhone();
  const isEdit = !!state.editingId;
  let costVal = null;
  if (isEdit) {
    costVal = (type === 'ticket'
      ? ($('m_CostTicket')?.value || $('m_Cost')?.value)
      : ($('m_Cost')?.value || $('m_CostTicket')?.value)) || null;
  } else if (type === 'visa') {
    const costPerAdt = Number($('m_VisaCost')?.value) || 0;
    costVal = costPerAdt > 0
      ? visaCostTotal({ pax: { adt: Number($('m_PaxAdt')?.value) || 0, chd: Number($('m_PaxChd')?.value) || 0 } }, costPerAdt)
      : null;
  } else if (type === 'ticket') {
    const breakdown = calcTicketPricingBreakdown();
    if (breakdown) {
      costVal = breakdown.cost > 0 || breakdown.profit > 0 ? breakdown.cost : null;
    } else {
      costVal = $('m_TicketCost')?.value || null;
    }
  }

  const ticketQuoteTotal = type === 'ticket' ? calcTicketPricingBreakdown()?.total : null;

  const body = {
    type,
    name: $('m_Name').value.trim(),
    phone,
    source: $('m_Source').value,
    status: isEdit ? $('m_Status').value : 'new',
    agent_id: (type === 'ticket' && !isEdit) ? null : (canPickAgent() ? ($('m_Agent').value || state.agent.id) : state.agent.id),
    inquiry_date: $('m_InquiryDate').value || todayISO(),
    followup_date: $('m_Followup').value || null,
    amount: (() => {
      if (type === 'ticket') {
        if (ticketQuoteTotal != null && ticketQuoteTotal > 0) return ticketQuoteTotal;
        return $('m_Amount')?.value || null;
      }
      const paxTotal = calcPaxRateTotal(type);
      if (paxTotal != null && paxTotal > 0) return paxTotal;
      return $('m_Amount')?.value || null;
    })(),
    is_custom: !!$('m_Custom')?.checked,
    custom_req: $('m_CustomReq')?.value.trim() || null,
    package_name: type === 'ticket' ? null : ($('m_Package').value || null),
    origin: $('m_Origin')?.value.trim().toUpperCase() || null,
    destination: $('m_Destination')?.value.trim().toUpperCase() || null,
    depart_date: $('m_Depart')?.value || null,
    return_date: $('m_Return')?.value || null,
    trip_type: type === 'ticket' ? $('m_TripType').value : null,
    airline: $('m_Airline')?.value.trim() || null,
    pnr: $('m_Pnr')?.value.trim() || null,
    passengers: $('m_Passengers')?.value || null,
    fare_amount: $('m_Fare')?.value || null,
    quote_amount: (() => {
      if (type === 'ticket') {
        if (ticketQuoteTotal != null && ticketQuoteTotal > 0) return ticketQuoteTotal;
        return $('m_Fare')?.value || $('m_Amount')?.value || null;
      }
      const paxTotal = calcPaxRateTotal(type);
      if (paxTotal != null && paxTotal > 0) return paxTotal;
      return $('m_Amount')?.value || null;
    })(),
    ticket_class: $('m_Class')?.value.trim() || null,
    cost_amount: costVal,
    sold_amount: (type === 'ticket'
      ? ($('m_SoldTicket')?.value || $('m_Sold')?.value)
      : ($('m_Sold')?.value || $('m_SoldTicket')?.value)) || null,
    note_text: $('m_Note')?.value.trim() || null,
    extra: (() => {
      const extra = buildExtraFromForm();
      if (type === 'ticket' && !isEdit) {
        extra.flow_version = 'ticket_v2';
        extra.pax_cost_rates = null;
        extra.pax_profit_rates = null;
        extra.pax_rates = null;
        extra.adt_pricing = null;
      }
      if (type === 'ticket' && ticketQuoteTotal != null && ticketQuoteTotal > 0) {
        extra.quoted_total = ticketQuoteTotal;
      }
      if (type === 'tour' && costVal != null && Number(costVal) > 0) {
        return syncTourAgentCost(extra, Number(costVal));
      }
      return extra;
    })(),
  };

  applyMultiCityToBody(body);
  applyAdtProfilesToBody(body);

  const ticketQuickIntake = type === 'ticket' && !isEdit;
  const intakeDetails = $('m_TicketIntakeDetails')?.value.trim() || '';

  if (ticketQuickIntake) {
    if (!body.phone) {
      showToast('Phone is required', 'warning');
      $('m_Phone')?.focus();
      return;
    }
    if (!body.source) {
      showToast('Select inquiry source', 'warning');
      $('m_Source')?.focus();
      return;
    }
    if (!intakeDetails) {
      showToast('Enter inquiry details', 'warning');
      $('m_TicketIntakeDetails')?.focus();
      return;
    }
    if (!body.name) {
      body.name = body.phone ? `Customer · ${body.phone}` : 'Ticket inquiry';
    }
    body.is_custom = true;
    body.custom_req = intakeDetails;
    body.extra = { ...body.extra, intake_details: intakeDetails };
  } else if (!body.name || !body.phone) {
    showToast('Name and phone are required', 'warning');
    $('m_Name')?.focus();
    return;
  }
  if (!ticketQuickIntake && !$('m_InquiryDate')?.value) {
    showToast('Inquiry date is required', 'warning');
    $('m_InquiryDate')?.focus();
    return;
  }
  if (type !== 'ticket' && !body.package_name) {
    showToast(type === 'visa' ? 'Select a visa type' : 'Select a package', 'warning');
    $('m_Package')?.focus();
    return;
  }
  if (isIndiaEvisaPackage(body.package_name)) {
    const { purpose, duration } = getIndiaEvisaSelections();
    if (!purpose || !duration) {
      showToast('Select eVisa category and validity', 'warning');
      return;
    }
  }
  if (type === 'ticket' && !ticketQuickIntake) {
    const profileErr = validateAdtProfiles(body);
    if (profileErr) {
      showToast(profileErr, 'warning');
      return;
    }
    if (body.extra?.per_adt_itineraries) {
      if (!body.extra.adt_profiles?.length) {
        showToast('Enter flight details for each adult', 'warning');
        return;
      }
    } else if (body.extra?.stu_profiles?.length && !(body.extra?.pax?.adt > 0)) {
      // Students only — flight details are on student cards.
    } else if (body.trip_type === 'multi') {
      const segs = body.extra?.flight_segments || [];
      if (segs.length < 2) {
        showToast('Add at least 2 flights for multi-city', 'warning');
        return;
      }
      if (segs.some((s) => !s.origin || !s.destination)) {
        showToast('Enter from and to for each flight', 'warning');
        return;
      }
      const scheduleErr = validateTicketSchedule(body);
      if (scheduleErr) {
        showToast(scheduleErr, 'warning');
        return;
      }
    } else {
      if (!body.origin || !body.destination) {
        showToast('Enter from and to airports', 'warning');
        return;
      }
      const scheduleErr = validateTicketSchedule(body);
      if (scheduleErr) {
        showToast(scheduleErr, 'warning');
        $('m_Depart')?.focus();
        return;
      }
    }
  }
  if (type === 'tour' && !isEdit) {
    const n = $('m_TourN')?.value;
    const d = $('m_TourD')?.value;
    if (!n || !d) {
      showToast('Enter tour nights and days (e.g. 4N 5D)', 'warning');
      $('m_TourN')?.focus();
      return;
    }
  }

  try {
    let clientMatchDecision = null;
    if (!state.editingId && !state.clientDupAcknowledged) {
      const matches = await fetchClientDuplicates();
      if (matches.length) {
        clientMatchDecision = await promptClientMatch(matches);
        if (!clientMatchDecision) return;
        state.clientDupAcknowledged = true;
        if (clientMatchDecision.action === 'same_client') {
          refreshSaveBodyAfterAutofill(body, phone);
          body.extra = {
            ...body.extra,
            same_client_new_inquiry: true,
            linked_inquiry_id: clientMatchDecision.match?.id || null,
          };
        }
      }
    }

    const btn = $('saveBtn');
    btn.disabled = true;

    if (clientMatchDecision?.action === 'merge') {
      const match = clientMatchDecision.match;
      const res = await api('/api/leads', { method: 'POST', body });
      showToast('Inquiry created — merge into the existing record', 'success');
      clearInquiryDraft(state.agent?.id);
      $('draftBanner')?.classList.add('hidden');
      $('addModal').classList.remove('open');
      btn.disabled = false;
      state.clientDupAcknowledged = false;
      await refresh();
      openMergeDupModal([
        {
          id: match.id,
          name: match.name,
          phone: match.phone,
          type: match.type,
          inquiry_date: match.inquiry_date,
          created_at: match.created_at,
        },
        {
          id: res.id,
          name: body.name,
          phone: body.phone,
          type: body.type,
          inquiry_date: body.inquiry_date,
          created_at: new Date().toISOString(),
        },
      ]);
      if ($('mergePrimarySelect')) {
        $('mergePrimarySelect').value = match.id;
        $('mergePrimarySelect').dispatchEvent(new Event('change'));
      }
      return;
    }

    if (state.editingId) {
      const editedId = state.editingId;
      await api('/api/leads/' + editedId, { method: 'PUT', body });
      showToast('Inquiry updated', 'success');
      state.editingId = null;
      btn.disabled = false;
      state.clientDupAcknowledged = false;
      $('addModal').classList.remove('open');
      await refreshAfterLeadChange({
        detailId: editedId,
        reloadDetail: state.detailId === editedId,
        reloadStats: false,
      });
      return;
    }

    await api('/api/leads', { method: 'POST', body });
    showToast(
      type === 'ticket' && body.agent_id == null
        ? 'Ticket saved to unassigned pool'
        : (body.extra?.same_client_new_inquiry ? 'New inquiry saved for same client' : 'Inquiry added'),
      'success',
    );
    clearInquiryDraft(state.agent?.id);
    $('draftBanner')?.classList.add('hidden');
    localStorage.setItem('mysky_last_type', type);
    if (body.source) localStorage.setItem('mysky_last_source', body.source);
    btn.disabled = false;
    state.clientDupAcknowledged = false;
    await refresh();
    if (andAnother && !state.editingId) {
      const keepType = type;
      const keepSource = body.source;
      resetForm();
      setInquiryMode(true);
      selectType(keepType);
      $('m_InquiryDate').value = todayISO();
      if (keepSource) $('m_Source').value = keepSource;
      if ($('m_Agent')) $('m_Agent').value = state.agent.id;
      const d = new Date();
      d.setDate(d.getDate() + 1);
      $('m_Followup').value = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Colombo' });
      syncAgentFieldUI();
      $('m_Name')?.focus();
    } else {
      clearInquiryDraft(state.agent?.id);
      $('draftBanner')?.classList.add('hidden');
      $('addModal').classList.remove('open');
    }
  } catch (ex) {
    $('saveBtn').disabled = false;
    showToast(ex.message, 'error');
  }
}

async function onStatusSelect() {
  if (!state.detailId) return;
  const next = $('dStatus').value;
  const prev = state.statusPrev || state.detailLead?.status || 'new';
  if (next === prev) return;
  if (!state.detailAccess?.can_edit) {
    $('dStatus').value = prev;
    showToast('You cannot change status on this inquiry', 'warning');
    return;
  }

  state.pendingStatus = next;
  state.detailStageTab = null;
  $('dStatus').value = prev;
  renderDetailStagePanel({ scroll: true, forceOpen: true });
  showStageFormModal(state.detailLead.type, next, state.detailLead);
}

function renderDetailStagePanel(opts = {}) {
  const panel = $('dStagePanel');
  const col = $('dStatusCol');
  const lead = state.detailLead;
  if (!panel || !lead) return;

  const pending = state.pendingStatus;
  const activeStatus = pending || lead.status;
  const type = lead.type || 'tour';
  const poolView = isDetailPoolIntakeView(lead);
  const canEdit = detailCanEdit(lead) && !poolView;
  const modalFormActive = statusModalOpen() && !!pending && statusHasStageForm(type, pending);
  const deferStageToModal = !!pending && statusHasStageForm(type, pending);

  if (col) col.classList.toggle('read-only', !canEdit);

  const tabs = getStatusStageTabs(type, lead.status, pending, lead);
  let viewStatus = state.detailStageTab || activeStatus;
  if (!tabs.some((t) => t.status === viewStatus)) {
    viewStatus = activeStatus;
    state.detailStageTab = null;
  }
  const tabMeta = tabs.find((t) => t.status === viewStatus) || tabs[tabs.length - 1];
  const isActiveTab = viewStatus === activeStatus;
  const isFuture = tabMeta?.state === 'future';

  const viewTitle = poolView && isActiveTab
    ? 'New inquiry'
    : (STATUS_CFG[viewStatus]?.label || STATUS_LABELS[viewStatus] || viewStatus);
  const tabTitle = $('dStageTabTitle');
  if (tabTitle) {
    tabTitle.textContent = pending && isActiveTab ? `Next: ${viewTitle}` : viewTitle;
    tabTitle.classList.toggle('is-pending', !!(pending && isActiveTab));
  }

  renderStatusStageTabs(type, lead, pending);

  const hasForm = statusHasStageForm(type, activeStatus);
  const ticketWideStages = new Set([
    'quotation_sent', 'temporary_booking', 'booking_confirmed', 'ticket_issued',
  ]);
  const wideStatus = isActiveTab ? activeStatus : viewStatus;
  const wide = wideStatus === 'quotation'
    || (type === 'ticket' && ticketWideStages.has(wideStatus));
  panel.classList.toggle('status-workflow-wide', wide);

  const showForm = isActiveTab && canEdit && hasForm && !isFuture && !modalFormActive && !deferStageToModal;
  const showPoolIntake = isActiveTab && poolView && !isFuture;
  panel.classList.toggle('has-stage-form', showForm || showPoolIntake);
  panel.classList.toggle('is-pool-intake', showPoolIntake);
  panel.classList.toggle('is-viewing-saved', !showForm && !isFuture);

  const urgent = !!opts.urgent || (state.stagePanelUrgent && activeStatus === 'tour_accepted_progress')
    || (!!lead.extra?.urgent_ticket_booking && activeStatus === 'tour_accepted_progress');
  panel.classList.toggle('detail-stage-urgent', urgent && isActiveTab);

  const incomplete = isActiveTab && !poolView && (!!pending || leadStageIncomplete(type, activeStatus, lead) || urgent);
  $('dStageIncompleteTag')?.classList.toggle('hidden', !incomplete);

  const viewBadge = $('dStageViewBadge');
  if (viewBadge) {
    if (showPoolIntake) {
      viewBadge.classList.add('hidden');
      viewBadge.classList.remove('is-editing', 'is-saved');
    } else if (showForm) {
      viewBadge.textContent = pending ? 'Next stage' : 'Editing';
      viewBadge.classList.remove('hidden', 'is-saved');
      viewBadge.classList.add('is-editing');
    } else if (!isFuture && !isActiveTab) {
      viewBadge.innerHTML = `${icon('check', 'stage-saved-check-icon')} Saved`;
      viewBadge.classList.remove('hidden', 'is-editing');
      viewBadge.classList.add('is-saved');
    } else if (!isFuture && isActiveTab && !showForm) {
      viewBadge.textContent = 'Current stage';
      viewBadge.classList.remove('hidden', 'is-editing', 'is-saved');
    } else {
      viewBadge.classList.add('hidden');
      viewBadge.classList.remove('is-editing', 'is-saved');
    }
  }

  const hintEl = $('dStageHint');
  const form = hasForm ? renderStageForm(type, activeStatus, lead) : { hint: '', html: '' };
  if (hintEl) {
    if (showPoolIntake) {
      hintEl.textContent = '';
      hintEl.classList.add('hidden');
    } else if (!isActiveTab && !isFuture) {
      hintEl.textContent = '';
      hintEl.classList.add('hidden');
    } else if (urgent && activeStatus === 'tour_accepted_progress' && isActiveTab) {
      hintEl.textContent = 'Client fully paid — book tickets urgently and enter visa details below.';
      hintEl.classList.remove('hidden');
    } else if (pending && isActiveTab) {
      hintEl.textContent = `Complete the fields below, then Save or Proceed to ${viewTitle}.`;
      hintEl.classList.remove('hidden');
    } else if (form.hint && incomplete) {
      hintEl.textContent = form.hint;
      hintEl.classList.remove('hidden');
    } else if (isFuture) {
      hintEl.textContent = 'Complete earlier stages first — this step is not active yet.';
      hintEl.classList.remove('hidden');
    } else {
      hintEl.textContent = '';
      hintEl.classList.add('hidden');
    }
  }

  if ($('stStageFields') && !statusModalOpen()) $('stStageFields').innerHTML = '';
  const wrap = $('dStageFields');
  const savedWrap = $('dStageSavedView');
  if (wrap) {
    wrap.innerHTML = showPoolIntake
      ? renderTicketPoolIntakeMainHtml(lead)
      : (showForm ? (form.html || '') : '');
  }
  if (savedWrap) {
    savedWrap.innerHTML = !showForm && !isFuture
      ? renderStageSavedViewHtml(type, viewStatus, lead)
      : '';
    savedWrap.classList.toggle('hidden', showForm || isFuture);
  }
  if (showForm) initStageForm(type, activeStatus, lead, 'dStageFields');

  $('dStageEmptyHint')?.classList.toggle('hidden', showForm || showPoolIntake || isFuture || !isActiveTab || !canEdit || hasForm);
  $('dStageFutureHint')?.classList.toggle('hidden', !isFuture);

  const showStageActions = isActiveTab && canEdit && !isFuture;
  const stageGroup = document.querySelector('.detail-action-stage');
  stageGroup?.classList.toggle('hidden', !showStageActions);
  document.querySelectorAll('.detail-action-vrule').forEach((el) => {
    const prev = el.previousElementSibling;
    const next = el.nextElementSibling;
    const touchesStage = prev === stageGroup || next === stageGroup;
    el.classList.toggle('hidden', touchesStage && !showStageActions);
  });
  const hideNewIntakeSave = type === 'ticket' && activeStatus === 'new' && isTicketQuickIntake(lead);
  $('dStageSaveBtn')?.classList.toggle('hidden', !showForm || hideNewIntakeSave || poolView);
  $('dStageCancelBtn')?.classList.toggle('hidden', !pending || !canEdit);

  updateNextStatusButton();

  if (opts.scroll) col?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

async function proceedToNextStatus() {
  if (!state.detailId || !state.detailLead) return;
  if (!detailCanEdit()) {
    showToast('You cannot change status on this inquiry', 'warning');
    return;
  }

  const type = state.detailLead.type;
  const activeStatus = state.pendingStatus || state.detailLead.status;

  if (statusHasStageForm(type, activeStatus)) {
    const err = validateStageFormClient(type, activeStatus, state.detailLead);
    if (err) {
      showToast(err, 'warning');
      return;
    }
    const body = { status: activeStatus, ...collectStageFormData(type, activeStatus, state.detailLead) };
    try {
      await patchStatus(body);
      state.pendingStatus = null;
    } catch (ex) {
      showToast(ex.message, 'error');
      return;
    }
  } else if (state.pendingStatus) {
    try {
      await patchStatus({ status: state.pendingStatus });
      state.pendingStatus = null;
    } catch (ex) {
      showToast(ex.message, 'error');
      return;
    }
  }

  const lead = state.detailLead;
  const next = getNextStatus(type, lead.status);
  if (!next) {
    showToast('Already at the final status', 'info');
    renderDetailStagePanel();
    return;
  }

  if (type === 'ticket' && next === 'quotation_sent' && lead.status !== 'new') {
    const q = ticketQuotedTotal(lead);
    if (!q || q <= 0) {
      showToast('Open Edit and enter quotation (est. cost + profit) before sending quote', 'warning');
      return;
    }
  }

  state.pendingStatus = next;
  if ($('dStatus')) $('dStatus').value = lead.status;
  renderDetailStagePanel({ forceOpen: true, scroll: true });
  showStageFormModal(type, next, state.detailLead);
}

function cancelDetailStage() {
  state.pendingStatus = null;
  state.detailStageTab = null;
  if (state.statusPrev) $('dStatus').value = state.statusPrev;
  if ($('dStageFields')) $('dStageFields').innerHTML = '';
  if ($('stStageFields')) $('stStageFields').innerHTML = '';
  $('statusModal')?.classList.remove('open');
  renderDetailStagePanel();
}

async function saveDetailStage() {
  const status = state.pendingStatus || state.detailLead?.status;
  if (!status || !state.detailId) return;
  const type = state.detailLead?.type || 'tour';
  const err = validateStageFormClient(type, status, state.detailLead);
  if (err) {
    showToast(err, 'warning');
    return;
  }

  const body = { status, ...collectStageFormData(type, status, state.detailLead) };
  try {
    await patchStatus(body);
    state.pendingStatus = null;
    state.stagePanelUrgent = false;
    $('statusModal')?.classList.remove('open');
    renderDetailStagePanel();
    updateNextStatusButton();
  } catch (ex) {
    showToast(ex.message, 'error');
  }
}

function openStatusPrompt(status) {
  state.pendingStatus = status;
  renderDetailStagePanel({ scroll: true, forceOpen: true });
  if (state.detailLead) showStageFormModal(state.detailLead.type, status, state.detailLead);
}

function cancelStatusChange() {
  cancelDetailStage();
  $('statusModal')?.classList.remove('open');
}

async function confirmStatusChange() {
  await saveDetailStage();
}

async function quickAdvanceLead(id, nextStatus) {
  const lead = state.leads.find((l) => l.id === id);
  if (!lead || !nextStatus) return;
  const type = lead.type;
  if (!canQuickAdvance(type, nextStatus)) {
    await openDetail(id);
    state.pendingStatus = nextStatus;
    if (state.detailLead) openStatusPrompt(nextStatus);
    return;
  }
  const prev = lead.status;
  lead.status = nextStatus;
  renderTable();
  try {
    const res = await api('/api/leads/' + id + '/status', { method: 'PATCH', body: { status: nextStatus } });
    applyPatchedLead(id, res?.lead);
    const label = STATUS_CFG[nextStatus]?.label || STATUS_LABELS[nextStatus] || nextStatus;
    showToast(`Advanced to ${label}`, 'success');
    // Row is already up to date; reconcile list/detail in the background.
    refreshAfterLeadChange({ detailId: id, reloadDetail: state.detailId === id }).catch(() => {});
  } catch (e) {
    lead.status = prev;
    renderTable();
    showToast(e.message || 'Could not advance status', 'error');
  }
}

// Fields the status endpoint can change; merged into the cached row and open
// detail so the UI reflects the save without waiting for a list refetch.
const PATCHED_LEAD_FIELDS = [
  'status', 'followup_date', 'amount', 'cost_amount', 'sold_amount', 'quote_amount', 'profit',
  'origin', 'destination', 'depart_date', 'return_date', 'pnr', 'airline', 'extra',
  'agent_id', 'last_edited_by', 'updated_at',
];

function applyPatchedLead(id, patched) {
  if (!patched || patched.id !== id) return;
  const merge = (target) => {
    if (!target) return;
    for (const k of PATCHED_LEAD_FIELDS) {
      if (patched[k] !== undefined) target[k] = patched[k];
    }
    if (patched.agent_id && patched.agent_id !== target.agent_id) {
      const a = state.agents?.find((x) => x.id === patched.agent_id);
      if (a) { target.agent_name = a.name; target.agent_color = a.color; }
    }
  };
  merge(state.leads.find((l) => l.id === id));
  if (state.detailId === id) merge(state.detailLead);
  renderTable();
}

async function patchStatus(body) {
  const prevStatus = state.detailLead?.status || state.statusPrev;
  const id = state.detailId;
  if (state.detailLead) state.detailLead.status = body.status;
  state.statusPrev = body.status;
  const row = state.leads.find((l) => l.id === id);
  if (row) {
    row.status = body.status;
    if (body.followup_date !== undefined) row.followup_date = body.followup_date;
  }
  if (state.detailId === id) {
    $('dStatus').value = body.status;
    updateDetailStatusHighlight(body.status);
    renderTable();
  }
  try {
    const res = await api('/api/leads/' + id + '/status', { method: 'PATCH', body });
    applyPatchedLead(id, res?.lead);
    showToast('Status updated', 'success');
    // Notes/history for the open detail and any filter membership changes
    // are reconciled in the background — the caller is not blocked on them.
    refreshAfterLeadChange({
      detailId: id,
      reloadDetail: state.detailId === id,
      reloadStats: false,
    }).catch(() => {});
  } catch (ex) {
    if (state.detailLead) state.detailLead.status = prevStatus;
    state.statusPrev = prevStatus;
    if (row) row.status = prevStatus;
    if ($('dStatus')) $('dStatus').value = prevStatus;
    updateDetailStatusHighlight(prevStatus);
    renderTable();
    showToast(ex.message, 'error');
  }
}

async function saveFollowupOnly() {
  if (!state.detailId || state.pendingStatus) return;
  if (!state.detailAccess?.can_edit) {
    $('dFollowup').value = state.detailLead?.followup_date || '';
    showToast('You cannot change follow-up on this inquiry', 'warning');
    return;
  }
  const prevDate = state.detailLead?.followup_date || '';
  const newDate = $('dFollowup').value || null;
  const status = state.statusPrev || state.detailLead?.status || $('dStatus').value;
  if (state.detailLead) state.detailLead.followup_date = newDate;
  const row = state.leads.find((l) => l.id === state.detailId);
  if (row) row.followup_date = newDate;
  renderTable();
  try {
    await api('/api/leads/' + state.detailId + '/status', {
      method: 'PATCH',
      body: { status, followup_date: newDate },
    });
    state.statusPrev = status;
  } catch (e) {
    $('dFollowup').value = prevDate;
    if (state.detailLead) state.detailLead.followup_date = prevDate;
    if (row) row.followup_date = prevDate;
    renderTable();
    showToast(e.message || 'Could not save follow-up', 'error');
  }
}

async function updateDetailStatus() {
  /* legacy — routed through onStatusSelect */
  await onStatusSelect();
}

async function deleteCurrent() {
  if (!state.detailId || !state.detailAccess?.can_edit || !confirm('Move this inquiry to the recycle bin?')) return;
  try {
    await api('/api/leads/' + state.detailId, { method: 'DELETE' });
    showToast('Moved to recycle bin', 'warning');
    closeDetail();
    await refresh();
  } catch (e) {
    showToast(e.message || 'Delete failed', 'error');
  }
}

async function addNote() {
  if (!state.detailAccess?.can_comment) {
    showToast('You cannot add notes on this inquiry', 'warning');
    return;
  }
  const text = $('noteInput').value.trim();
  if (!text || !state.detailId) return;
  $('noteInput').value = '';
  try {
    await api('/api/leads/' + state.detailId + '/notes', { method: 'POST', body: { text } });
    state.detailHistory = null;
    state.historyItems = [];
    await reloadDetailQuiet(state.detailId);
    showToast('Note added', 'success');
  } catch (e) {
    $('noteInput').value = text;
    showToast(e.message || 'Could not add note', 'error');
  }
}

function openCallModal() {
  if (!state.detailId) return;
  if (!state.detailAccess?.can_comment && !state.detailAccess?.can_log_call) {
    showToast('You cannot log calls on this inquiry', 'warning');
    return;
  }
  const sub = $('callModalSub');
  if (sub) {
    const name = state.detailLead?.name || $('dName')?.textContent || 'client';
    sub.textContent = `Log your conversation with ${name}`;
  }
  $('clDate').value = todayISO();
  $('clTime').value = nowTime();
  $('clNotes').value = '';
  $('clResponse').value = '';
  $('clNext').value = '';
  $('clNext')?.closest('.liquid-form-section')?.classList.toggle('hidden', !state.detailAccess?.can_edit);
  $('callModal').classList.add('open');
}

async function saveCall() {
  if (!state.detailId) return;
  const notes = $('clNotes').value.trim();
  const response = $('clResponse').value.trim();
  if (!notes && !response) {
    showToast('Enter discussion notes or customer response', 'warning');
    $('clNotes')?.focus();
    return;
  }
  try {
    await api('/api/leads/' + state.detailId + '/calls', {
      method: 'POST',
      body: {
        call_date: $('clDate').value,
        call_time: $('clTime').value,
        notes,
        response,
        next_followup: $('clNext').value || null,
      },
    });
    $('callModal').classList.remove('open');
    showToast('Call logged', 'success');
    state.detailHistory = null;
    state.historyItems = [];
    await refreshAfterLeadChange({ reloadStats: false });
  } catch (e) {
    showToast(e.message || 'Could not log call', 'error');
  }
}

async function applyBulk() {
  const status = $('bulkStatus').value;
  const followupDate = $('bulkFollowupDate')?.value;
  if (!status || !state.selected.size) return;
  if (status === 'followup' && !followupDate) {
    showToast('Pick a follow-up date for bulk update', 'warning');
    return;
  }
  try {
    const n = state.selected.size;
    const ids = [...state.selected];
    const result = await api('/api/leads/bulk-status', {
      method: 'POST',
      body: { ids, status, followup_date: followupDate || null },
    });
    state.selected.clear();
    const skipped = result.skipped || 0;
    const updated = result.updated || 0;
    let msg = `Updated ${updated} of ${n}`;
    if (skipped > 0) {
      const reasons = result.skip_reasons ? Object.entries(result.skip_reasons).map(([k, v]) => `${k}: ${v}`).join('; ') : '';
      msg += ` (${skipped} skipped${reasons ? ' — ' + reasons : ''})`;
    }
    showToast(msg, updated ? 'success' : 'warning');
    await refresh();
  } catch (e) {
    showToast(e.message || 'Bulk update failed', 'error');
  }
}

function toggleSelectAll(checked) {
  state.leads.forEach((l) => {
    if (!canBulkEditLead(l)) return;
    if (checked) state.selected.add(l.id);
    else state.selected.delete(l.id);
  });
  renderTable();
  updateBulkBar();
}

async function exportCsv() {
  try {
  const data = await api('/api/export');
  const leads = data.leads || [];
  if (leads.length > 2000) {
    showToast(`Exporting ${leads.length} rows — this may take a moment`, 'warning');
  }
  const headers = ['Name','Phone','Type','Package','Agent','Status','Source','Inquiry Date','Follow-up','Amount','Destination','Airline','PNR','Quote'];
  const rows = leads.map((l) => [
    l.name, l.phone, l.type, l.package_name || '', l.agent_name || '', l.status, l.source || '',
    l.inquiry_date || '', l.followup_date || '', l.amount ?? '', l.destination || '', l.airline || '',
    l.pnr || '', l.quote_amount ?? '',
  ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
  const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mysky_leads_${todayISO()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  showToast('Export downloaded', 'success');
  } catch (e) {
    showToast(e.message || 'Export failed', 'error');
  }
}

function debounce(fn, ms) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

const BALANCE_COLLECT_PREFIX = 'BALANCE_COLLECT|';
const URGENT_TKT_PREFIX = 'URGENT_TKT|';
const BOOKING_EXPIRY_PREFIX = 'BOOKING_EXPIRY|';
const BOOKING_EXPIRED_PREFIX = 'BOOKING_EXPIRED|';

function isBalanceCollectReminder(message) {
  return String(message || '').startsWith(BALANCE_COLLECT_PREFIX);
}

function isUrgentTicketReminder(message) {
  return String(message || '').startsWith(URGENT_TKT_PREFIX);
}

function isBookingExpiryReminder(message) {
  return String(message || '').startsWith(BOOKING_EXPIRY_PREFIX);
}

function isBookingExpiredReminder(message) {
  return String(message || '').startsWith(BOOKING_EXPIRED_PREFIX);
}

function reminderDisplayMessage(message) {
  const raw = String(message || '');
  if (raw.startsWith(BALANCE_COLLECT_PREFIX)) return raw.slice(BALANCE_COLLECT_PREFIX.length);
  if (raw.startsWith(URGENT_TKT_PREFIX)) return raw.slice(URGENT_TKT_PREFIX.length);
  if (raw.startsWith(BOOKING_EXPIRY_PREFIX)) return raw.slice(BOOKING_EXPIRY_PREFIX.length);
  if (raw.startsWith(BOOKING_EXPIRED_PREFIX)) return raw.slice(BOOKING_EXPIRED_PREFIX.length);
  return raw;
}

let remindersPausedWhileHidden = false;
if (typeof document !== 'undefined') {
  // Resume polling immediately when the tab comes back into view.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && remindersPausedWhileHidden) {
      remindersPausedWhileHidden = false;
      checkReminders();
    }
  });
}

async function checkReminders() {
  if (!state.agent) return;
  // Background tabs skip the poll; cron on the server still creates reminders.
  if (document.hidden) { remindersPausedWhileHidden = true; return; }
  try {
    const data = await api('/api/reminders?due=1');
    const due = data.reminders || [];
    const banner = $('reminderBanner');
    if (!banner) return;

    if (!due.length) {
      banner.classList.add('hidden');
      banner.innerHTML = '';
      return;
    }
    banner.classList.remove('hidden');

    const newOnes = due.filter((r) => !state.alertedReminders.has(r.id));
    if (newOnes.length && state.soundOn) playReminderSound();
    due.forEach((r) => state.alertedReminders.add(r.id));

    banner.innerHTML = due.map((r) => {
      const isBalance = isBalanceCollectReminder(r.message);
      const isBookingExpiry = isBookingExpiryReminder(r.message);
      const isBookingExpired = isBookingExpiredReminder(r.message);
      const isUrgent = isUrgentTicketReminder(r.message) || isBookingExpiry || isBookingExpired;
      const msg = reminderDisplayMessage(r.message);
      const label = isBalance ? 'Balance due' : isBookingExpired ? 'Booking expired' : isBookingExpiry ? 'Booking expiring' : isUrgent ? 'Urgent' : 'Reminder';
      const balanceBtns = isBalance ? `
        <div class="btn-bar reminder-action-bar">
          <button class="btn btn-ghost btn-sm" onclick="mysky.openBalancePostpone('${esc(r.id)}')">Postpone</button>
          <button class="btn btn-primary btn-sm" onclick="mysky.openBalanceCollect('${esc(r.lead_id)}','${esc(r.id)}')">Payment collected</button>
          <button class="btn btn-ghost btn-sm" onclick="mysky.openDetailFromReminder('${esc(r.lead_id)}','')">Open inquiry</button>
        </div>` : `
        <div class="btn-bar btn-bar-2">
          <button class="btn btn-ghost" onclick="mysky.openDetailFromReminder('${esc(r.lead_id)}','${esc(r.id)}')">Open inquiry</button>
          <button class="btn btn-primary" onclick="mysky.ackReminder('${esc(r.id)}')">Dismiss</button>
        </div>`;
      return `
      <div class="reminder-alert${isBalance ? ' reminder-alert-balance' : ''}${isUrgent ? ' reminder-alert-urgent' : ''}" data-rm="${esc(r.id)}">
        <h4><span class="reminder-priority">${icon('bell')} ${label}</span></h4>
        <p><strong>${esc(r.lead_name)}</strong>${msg ? ' — ' + esc(msg) : ''}<br>
        <span style="color:var(--fg-dim)">${esc(r.lead_phone || '')} · due ${esc(fmtDateTime(r.remind_at))}</span></p>
        ${balanceBtns}
      </div>`;
    }).join('');
  } catch {
    /* reminders table may not exist yet before migration */
  }
}

async function saveReminder() {
  if (!state.detailId || !state.detailAccess?.can_edit) {
    showToast('You cannot set reminders on this inquiry', 'warning');
    return;
  }
  const remind_at = $('rmDateTime')?.value;
  const message = $('rmMessage')?.value?.trim() || '';
  if (!remind_at) {
    showToast('Pick a reminder date & time', 'warning');
    return;
  }
  try {
    await api('/api/reminders', {
      method: 'POST',
      body: { lead_id: state.detailId, remind_at, message },
    });
    showToast('Reminder set', 'success');
    $('rmDateTime').value = '';
    $('rmMessage').value = '';
    loadLeadReminders(state.detailId);
    checkReminders();
  } catch (e) {
    showToast(e.message, 'error');
  }
}

async function loadLeadReminders(leadId) {
  const box = $('leadReminders');
  const empty = $('leadRemindersEmpty');
  if (!box) return;
  try {
    const data = await api('/api/reminders?lead_id=' + encodeURIComponent(leadId));
    const list = (data.reminders || []).filter((r) => !r.acknowledged);
    if (list.length) {
      box.innerHTML = list.map((r) => `
        <div class="lead-reminder-item">
          ${icon('bell', '', 'color:var(--red)')}
          <span class="lead-reminder-item-text">${esc(fmtDateTime(r.remind_at))}${r.message ? ' — ' + esc(reminderDisplayMessage(r.message)) : ''}</span>
          <button type="button" class="btn btn-ghost btn-sm" onclick="mysky.deleteReminder('${esc(r.id)}')" aria-label="Delete reminder">&times;</button>
        </div>`).join('');
      box.classList.remove('hidden');
      empty?.classList.add('hidden');
    } else {
      box.innerHTML = '';
      box.classList.add('hidden');
      empty?.classList.toggle('hidden', !state.detailAccess?.can_edit);
    }
  } catch {
    box.innerHTML = '';
    box.classList.add('hidden');
    empty?.classList.add('hidden');
  }
}

async function ackReminder(id) {
  try {
    await api('/api/reminders/' + id + '/ack', { method: 'POST' });
    state.alertedReminders.delete(id);
    checkReminders();
    showToast('Reminder dismissed', 'success');
  } catch (e) {
    showToast(e.message || 'Could not dismiss reminder', 'error');
  }
}

async function deleteReminder(id) {
  try {
    await api('/api/reminders/' + id, { method: 'DELETE' });
    if (state.detailId) loadLeadReminders(state.detailId);
    checkReminders();
  } catch (e) {
    showToast(e.message || 'Could not delete reminder', 'error');
  }
}

async function openDetailFromReminder(leadId, rmId) {
  await openDetail(leadId);
  if (rmId) await ackReminder(rmId);
}

function openBalanceCollect(leadId, reminderId) {
  const lead = state.leads.find((l) => l.id === leadId) || state.detailLead;
  const due = leadOutstandingBalance(lead) || lead?.extra?.balance_due || 0;
  state.balanceCollectCtx = { leadId, reminderId, balanceDue: due };
  if ($('bcAmount')) $('bcAmount').value = due > 0 ? String(Math.round(due)) : '';
  if ($('bcRemainHint')) {
    $('bcRemainHint').textContent = due > 0
      ? `Outstanding balance: ${fmtAmt(due)}. If partial, enter amount received — status advances when fully paid.`
      : '';
  }
  $('balanceCollectModal')?.classList.add('open');
}

async function submitBalanceCollection() {
  const ctx = state.balanceCollectCtx;
  if (!ctx?.leadId) return;
  const amount = Number($('bcAmount')?.value);
  if (!Number.isFinite(amount) || amount <= 0) {
    showToast('Enter amount collected', 'warning');
    return;
  }
  try {
    $('bcConfirmBtn').disabled = true;
    const res = await api(`/api/leads/${ctx.leadId}/collect-balance`, {
      method: 'POST',
      body: { amount, reminder_id: ctx.reminderId || null },
    });
    closeModal('balanceCollectModal');
    state.balanceCollectCtx = null;
    if (res.auto_advanced) {
      state.stagePanelUrgent = true;
      showToast('Fully paid — advanced to TKT & visa. Enter ticket & visa details below.', 'success');
    } else {
      showToast(`Payment recorded. Remaining: ${fmtAmt(res.balance_due)}`, 'success');
    }
    await refreshAfterLeadChange({
      detailId: ctx.leadId,
      reloadDetail: state.detailId === ctx.leadId,
      reloadStats: false,
    });
    if (state.detailId === ctx.leadId && res.auto_advanced) {
      renderDetailStagePanel({ urgent: true, scroll: true, forceOpen: true });
    }
    checkReminders();
  } catch (e) {
    showToast(e.message || 'Could not record payment', 'error');
  } finally {
    $('bcConfirmBtn').disabled = false;
  }
}

function openBalancePostpone(reminderId) {
  state.balancePostponeId = reminderId;
  const d = new Date();
  d.setDate(d.getDate() + 1);
  if ($('bpDate')) $('bpDate').value = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Colombo' });
  $('balancePostponeModal')?.classList.add('open');
}

async function submitBalancePostpone() {
  const id = state.balancePostponeId;
  const date = $('bpDate')?.value;
  if (!id || !date) {
    showToast('Pick a new date', 'warning');
    return;
  }
  try {
    await api(`/api/reminders/${id}/postpone`, { method: 'POST', body: { remind_at: date } });
    closeModal('balancePostponeModal');
    state.balancePostponeId = null;
    showToast('Reminder postponed', 'success');
    checkReminders();
    await refreshListOnly({ silent: true });
    if (state.detailId) await reloadDetailQuiet(state.detailId);
  } catch (e) {
    showToast(e.message || 'Could not postpone', 'error');
  }
}

const HISTORY_AUDIT_LABELS = {
  'lead.create': 'Inquiry created',
  'lead.update': 'Inquiry updated',
  'lead.status': 'Status changed',
  'lead.call': 'Call logged',
  'lead.delete': 'Moved to recycle bin',
  'lead.request_edit': 'Edit access requested',
  'lead.takeover': 'Inquiry taken over',
  'lead.grant_edit': 'Edit access granted',
  'lead.deny_edit': 'Edit access denied',
};

function parseAuditDetail(detailRaw) {
  try {
    return typeof detailRaw === 'string' ? JSON.parse(detailRaw || '{}') : (detailRaw || {});
  } catch {
    return {};
  }
}

function isAutoStatusNote(text) {
  if (!text) return false;
  const prefixes = [
    ...Object.values(STATUS_LABELS).map((l) => `${l}: `),
    'Client not interested: ',
    'Reopened: ',
  ];
  return prefixes.some((p) => text.startsWith(p));
}

function extractStatusNoteExtra(text, status) {
  if (!text) return '';
  const label = STATUS_LABELS[status] || status;
  const prefixes = [`${label}: `, 'Client not interested: ', 'Reopened: '];
  for (const p of prefixes) {
    if (text.startsWith(p)) {
      const rest = text.slice(p.length).trim();
      if (!rest || rest === label || rest === STATUS_LABELS[status]) return '';
      return rest;
    }
  }
  return '';
}

function formatAuditHistoryLine(action, detailRaw, lead) {
  const detail = parseAuditDetail(detailRaw);
  if (action === 'lead.status' && detail.status) {
    return statusHistoryTitle(lead?.type || 'tour', detail.status, detail.from);
  }
  return HISTORY_AUDIT_LABELS[action] || action.replace(/^lead\./, '').replace(/_/g, ' ');
}

function statusHistoryDescription(auditDetail, lead, recordedAt) {
  const status = auditDetail.status;
  if (!status || !lead) return '';
  const stageData = auditDetail.stage_data
    ?? lead.extra?.stage_data?.[status]
    ?? {};
  let text = formatStageHistoryDescription(lead.type, status, stageData, lead, recordedAt);
  const noteExtra = extractStatusNoteExtra(auditDetail.note_extra, status);
  if (noteExtra) text = text ? `${text}\nNotes: ${noteExtra}` : `Notes: ${noteExtra}`;
  if (!text) text = getStageHint(lead.type, status) || '';
  return text;
}

function findLinkedStatusNote(notes, status, auditTime) {
  const label = STATUS_LABELS[status];
  if (!label) return null;
  const prefix = `${label}: `;
  let best = null;
  let bestDelta = Infinity;
  for (const n of notes || []) {
    if (!n.text?.startsWith(prefix)) continue;
    const delta = Math.abs(new Date(n.created_at).getTime() - auditTime);
    if (delta < bestDelta && delta < 120000) {
      best = n;
      bestDelta = delta;
    }
  }
  return best;
}

function buildHistoryItems(history) {
  const items = [];
  const l = history.lead;
  const audits = history.audit_events || [];

  const hasCreateAudit = audits.some((a) => a.action === 'lead.create');
  if (l?.created_at && !hasCreateAudit) {
    items.push({
      kind: 'event',
      time: l.created_at,
      title: 'Inquiry created',
      text: `${l.type || 'inquiry'} · ${l.package_name || l.origin || '—'}`,
      who: l.agent_name || 'Agent',
      icon: 'plus',
      color: '#60a5fa',
    });
  }

  audits.forEach((a) => {
    const detail = parseAuditDetail(a.detail);
    let text = '';
    let title = formatAuditHistoryLine(a.action, a.detail, l);
    let kind = 'audit';
    let itemIcon = 'history';
    let color = a.actor_color || '#8A63D2';

    if (a.action === 'lead.create') {
      text = `${l.type || 'inquiry'} · ${l.package_name || l.origin || '—'}`;
    } else if (a.action === 'lead.status' && detail.status) {
      kind = 'status';
      title = formatAuditHistoryLine(a.action, a.detail, l);
      const auditTime = new Date(a.created_at).getTime();
      const linkedNote = findLinkedStatusNote(history.notes, detail.status, auditTime);
      if (linkedNote) detail.note_extra = linkedNote.text;
      text = statusHistoryDescription(detail, l, a.created_at);
      itemIcon = 'tag';
      color = PIPELINE_COLORS[detail.status] || '#8A63D2';
    } else if (a.action === 'lead.call') {
      itemIcon = 'phone';
      color = '#4fd6e8';
    }

    items.push({
      kind,
      time: a.created_at,
      title,
      text,
      who: a.actor_name || 'System',
      icon: itemIcon,
      color,
      status: detail.status || null,
    });
  });

  (history.notes || []).forEach((n) => {
    if (isAutoStatusNote(n.text)) return;
    const isPricing = String(n.text || '').startsWith('Pricing updated:');
    items.push({
      kind: isPricing ? 'pricing' : 'note',
      time: n.created_at,
      title: isPricing ? 'Pricing updated' : 'Note',
      text: n.text,
      who: n.author_name || 'Agent',
      icon: isPricing ? 'tag' : 'pen',
      color: n.author_color || '#a78bfa',
    });
  });
  (history.call_logs || []).forEach((c) => {
    const text = [c.notes && `Discussion: ${c.notes}`, c.response && `Response: ${c.response}`].filter(Boolean).join(' · ');
    items.push({
      kind: 'call',
      time: `${c.call_date} ${c.call_time || ''}`.trim(),
      title: 'Call log',
      text,
      who: c.agent_name || 'Agent',
      icon: 'phone',
      color: c.agent_color || '#4fd6e8',
    });
  });
  items.sort((a, b) => timeSortKey(b.time) - timeSortKey(a.time));
  return items;
}

function timeSortKey(time) {
  if (String(time || '').includes('T')) {
    const iso = new Date(time).getTime();
    if (!Number.isNaN(iso)) return iso;
  }
  return activitySortKey({ kind: 'call', time });
}

function renderHistoryTimeline(items) {
  const timeline = $('historyTimeline');
  if (!timeline) return;
  const filtered = items.filter((item) => historyItemMatchesFilter(item, state.historyFilter, state.historySearch));
  const meta = $('historyFilterMeta');
  if (meta) {
    meta.textContent = filtered.length === items.length
      ? `${items.length} ${items.length === 1 ? 'entry' : 'entries'}`
      : `Showing ${filtered.length} of ${items.length} entries`;
  }
  timeline.innerHTML = filtered.length
    ? filtered.map((item) => `
      <div class="history-item history-${esc(item.kind)}${item.kind === 'status' ? ' history-item-status' : ''}">
        <div class="history-item-icon" style="--hi-color:${esc(item.color)}">${icon(item.icon)}</div>
        <div class="history-item-body">
          <div class="history-item-top">
            <strong>${esc(item.title)}</strong>
            <span>${esc(fmtDateTime(item.time))}</span>
          </div>
          <div class="history-item-meta">${esc(item.who || 'Agent')}</div>
          ${item.text ? `<div class="history-item-text history-item-detail">${esc(item.text)}</div>` : '<div class="history-item-text history-item-detail history-item-empty">No details recorded for this step.</div>'}
        </div>
      </div>`).join('')
    : `<div class="history-empty">${items.length ? 'No entries match this filter.' : 'No history yet.'}</div>`;
}

function historyItemMatchesFilter(item, filter, search) {
  if (filter === 'status' && item.kind !== 'status') return false;
  if (filter === 'note' && item.kind !== 'note') return false;
  if (filter === 'call' && item.kind !== 'call') return false;
  if (filter === 'update' && (item.kind === 'status' || item.kind === 'note' || item.kind === 'call')) return false;
  if (search) {
    const q = search.toLowerCase();
    const hay = `${item.title || ''} ${item.text || ''} ${item.who || ''}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

function updateHistoryFilterButtons() {
  document.querySelectorAll('[data-history-filter]').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.historyFilter === state.historyFilter);
  });
}

function setHistoryFilter(filter) {
  state.historyFilter = filter || 'all';
  updateHistoryFilterButtons();
  renderHistoryTimeline(state.historyItems);
}

function renderHistoryView(history) {
  const l = history.lead;
  const summary = $('historySummary');
  if ($('historyModalSub')) {
    $('historyModalSub').textContent = `${l.name} · ${l.phone || ''}`;
  }
  if (summary) {
    const sc = STATUS_CFG[l.status] || STATUS_CFG.new;
    summary.innerHTML = `
      <div class="history-stat"><span>Status</span><strong class="badge ${sc.cls}">${esc(sc.label)}</strong></div>
      <div class="history-stat"><span>Agent</span><strong>${esc(l.agent_name || '—')}</strong></div>
      <div class="history-stat"><span>Created</span><strong>${esc(fmtDateTime(l.created_at))}</strong></div>
      <div class="history-stat"><span>Updated</span><strong>${esc(fmtDateTime(l.updated_at))}</strong></div>`;
  }
  state.historyItems = buildHistoryItems(history);
  renderHistoryTimeline(state.historyItems);
}

async function openHistoryView() {
  if (!state.detailId) return;
  try {
    let history = state.detailHistory;
    if (!history || history.lead?.id !== state.detailId) {
      const data = await api('/api/leads/' + state.detailId);
      history = {
        lead: data.lead,
        notes: data.notes || [],
        call_logs: data.call_logs || [],
        audit_events: data.audit_events || [],
      };
      state.detailHistory = history;
    }
    state.historyFilter = 'all';
    state.historySearch = '';
    if ($('historySearch')) $('historySearch').value = '';
    updateHistoryFilterButtons();
    renderHistoryView(history);
    $('historyModal')?.classList.add('open');
  } catch (e) {
    showToast(e.message || 'Could not load history', 'error');
  }
}

function showForcePasswordModal() {
  const m = $('forcePassModal');
  if (!m) return;
  m.style.display = '';
  m.classList.add('open');
  $('appShell')?.classList.add('force-pass-lock');
}

function hideForcePasswordModal() {
  const m = $('forcePassModal');
  if (!m) return;
  m.style.display = 'none';
  m.classList.remove('open');
  $('appShell')?.classList.remove('force-pass-lock');
}

async function saveForcedPassword() {
  const next = $('fpNew')?.value || '';
  const confirm = $('fpConfirm')?.value || '';
  if (next.length < 8) {
    showToast('Password must be at least 8 characters', 'warning');
    return;
  }
  if (next !== confirm) {
    showToast('Passwords do not match', 'warning');
    return;
  }
  try {
    await api('/api/me/password', {
      method: 'POST',
      body: { new_password: next },
    });
    state.agent.must_change_password = false;
    hideForcePasswordModal();
    resetPasswordInput($('fpNew'));
    resetPasswordInput($('fpConfirm'));
    showToast('Password updated — welcome!', 'success');
  } catch (ex) {
    showToast(ex.message, 'error');
  }
}

function exposeMyskyGlobal() {
  window.mysky = {
    logout, openAddModal, closeDetail, toggleDetailSidebar, saveLead, onTypeChange, toggleCustom,
    onTripTypeChange, toggleMoreFields, selectType, onPackageChange,
    updatePaxPreview, updateTourDurationPreview,
    updateDetailStatus, onStatusSelect, cancelStatusChange, confirmStatusChange, saveDetailStage, cancelDetailStage, proceedToNextStatus, saveFollowupOnly,
    deleteCurrent, addNote, openCallModal, saveCall,
    exportCsv, refresh, setDepartmentScope,
    toggleAutoRefresh, toggleDensity, toggleTheme, toggleSound,
    saveReminder, ackReminder, deleteReminder, openDetailFromReminder,
    prevPage: () => { if (state.page > 1) { state.page--; persistFilter(); refreshListOnly(); } },
    nextPage: () => { if (state.page < state.total_pages) { state.page++; persistFilter(); refreshListOnly(); } },
    closeModal,
    openWhatsAppDetail: () => {
      const url = whatsappUrl($('dPhone').textContent);
      if (url) window.open(url, '_blank');
    },
    copyPhone: async () => {
      const phone = $('dPhone')?.textContent?.trim();
      if (!phone || phone === '—') return;
      try {
        await navigator.clipboard.writeText(phone);
        showToast('Phone copied', 'success');
      } catch {
        showToast('Could not copy', 'error');
      }
    },
    editFromDetail: async () => {
      if (!state.detailId) return;
      if (!state.detailAccess?.can_edit) {
        showToast('Request edit access or take over this inquiry first', 'warning');
        return;
      }
      if (isLeadsExplorerPage()) {
        try {
          await ensureInquiryModalShell();
          initInquiryModal();
        } catch (e) {
          showToast(e.message || 'Could not load edit form', 'error');
          return;
        }
      }
      await openEditModal(state.detailId);
    },
    requestEditAccess, submitEditRequest, takeOverLead, claimLead, approveAccessRequest, denyAccessRequest, openDetail,
    openHistoryView, setHistoryFilter, updatePaxQuoteTotal, closeClientMatchModal, openMergeDupModal,
    openBalanceCollect, submitBalanceCollection, openBalancePostpone, submitBalancePostpone,
  };
}

function bindDetailHostEvents() {
  initDetailPopovers();
  $('dStageTabs')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-stage-tab]');
    if (!btn || btn.disabled) return;
    selectDetailStageTab(btn.dataset.stageTab);
  });
  $('dFollowup')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveFollowupOnly(); }
  });
  $('dStageFieldsWrap')?.addEventListener('input', () => updateNextStatusButton());
  $('dStageFieldsWrap')?.addEventListener('change', () => updateNextStatusButton());
  $('mergeDupConfirmBtn')?.addEventListener('click', confirmMergeDuplicates);
  $('fpSaveBtn')?.addEventListener('click', saveForcedPassword);
  document.querySelectorAll('[data-history-filter]').forEach((btn) => {
    btn.addEventListener('click', () => setHistoryFilter(btn.dataset.historyFilter));
  });
  const debouncedHistorySearch = debounce(() => {
    state.historySearch = $('historySearch')?.value?.trim() || '';
    renderHistoryTimeline(state.historyItems);
  }, 200);
  $('historySearch')?.addEventListener('input', debouncedHistorySearch);

  document.querySelectorAll('.modal-overlay').forEach((overlay) => {
    overlay.addEventListener('click', (e) => {
      if (e.target !== overlay) return;
      const id = overlay.id;
      if (id === 'statusModal') cancelStatusChange();
      else if (id) closeModal(id);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if ($('historyModal')?.classList.contains('open')) closeModal('historyModal');
    else if ($('callModal')?.classList.contains('open')) closeModal('callModal');
    else if ($('statusModal')?.classList.contains('open')) cancelStatusChange();
    else if ($('editRequestModal')?.classList.contains('open')) closeModal('editRequestModal');
    else if ($('balanceCollectModal')?.classList.contains('open')) closeModal('balanceCollectModal');
    else if ($('balancePostponeModal')?.classList.contains('open')) closeModal('balancePostponeModal');
    else if ($('mergeDupModal')?.classList.contains('open')) closeModal('mergeDupModal');
    else if ($('addModal')?.classList.contains('open')) closeModal('addModal');
    else if (!$('dReminderPopover')?.classList.contains('hidden') || !$('dOverflowMenu')?.classList.contains('hidden')) {
      closeDetailPopovers();
    } else if ($('detailPanel')?.classList.contains('open')) closeDetail();
  });
}

export async function initDetailHost(hostState, callbacks = {}) {
  bindDetailHost(hostState, callbacks);
  initPasswordToggles();
  initDetailSwipe($('detailPanel'), $('detailOverlay'), closeDetail);
  if (isLeadsExplorerPage()) {
    ensureInquiryModalReady().catch((e) => console.error(e));
  }
  bindDetailHostEvents();
  exposeMyskyGlobal();
  if (state.agent?.must_change_password) showForcePasswordModal();
  if (isLeadsExplorerPage()) {
    checkReminders();
    setInterval(() => { if (state.agent) checkReminders(); }, 30000);
  }
  window.__myskyOn401 = () => {
    showToast('Session expired — please sign in again', 'warning');
    location.href = '/';
  };
}

export { openDetail, closeDetail };

if (!isLeadsExplorerPage()) {
  boot();
  setInterval(() => { if (state.agent) checkReminders(); }, 30000);
}
